/**
 * Static blog posts data.
 *
 * To add a new post:
 *   1. Add an object below with slug, title, description, date, readMinutes, and content
 *   2. Update /public/sitemap.xml with the new URL
 *
 * `content` is an array of blocks — each block is { type: "h2"|"h3"|"p"|"ul"|"quote", text|items }.
 * Keeping content as structured data (not raw HTML) means React renders it safely with
 * no dangerouslySetInnerHTML and we control the typography uniformly.
 */

export const blogPosts = [
  {
    slug: "five-minute-weekly-google-analytics-review",
    title: "The 5-minute weekly Google Analytics review (a busy founder's habit)",
    description:
      "Most founders avoid GA4 because they don't have a system. Here's the exact 5-minute Monday morning routine that gives you 90% of the insight without becoming an analyst — including the four numbers to check and a printable checklist.",
    date: "2026-04-05",
    readMinutes: 10,
    keywords: [
      "weekly Google Analytics review",
      "how often to check GA4",
      "Google Analytics for busy people",
      "5 minute analytics review",
      "GA4 weekly check",
      "weekly traffic report",
      "GA4 habit",
    ],
    author: { name: "Nicholas Steer", role: "Founder, Plainly" },
    content: [
      {
        type: "p",
        text:
          "Most founders fall into one of two analytics traps. The first is the obsessive trap: opening Google Analytics fifteen times a day, panicking at every dip, making rash decisions based on small samples. The second is the avoidance trap: opening GA4 once a quarter, getting overwhelmed, closing the tab, and going months without looking at it again.",
      },
      {
        type: "p",
        text:
          "Neither one builds a real business. What works is a small, repeatable, weekly habit — one that takes five minutes, doesn't require any specialized knowledge, and gives you 90% of what you'd get from spending two hours pretending to be an analyst. That's what this post is about: the exact routine, the exact four numbers, the exact questions, and a printable checklist at the end you can stick on your monitor.",
      },
      {
        type: "p",
        text:
          "I built this routine for myself before I built Plainly. The product exists because the routine works — Plainly just automates it so I don't even have to remember to do it. But if you'd rather DIY it for free, this is how.",
      },
      { type: "h2", text: "When to do it: Monday morning, before email" },
      {
        type: "p",
        text:
          "Habits stick when they're attached to something else you already do. The best slot for an analytics review is Monday morning, before you open email or Slack. Three reasons:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Monday gives you a clean week-over-week comparison",
            body:
              "By Monday, last week is complete. You can compare it cleanly against the week before. Wednesdays force you to either compare partial weeks or roll back to last Monday, both of which are mentally annoying.",
          },
          {
            label: "Before email means you're not already in reactive mode",
            body:
              "Once you've opened email, your brain is on someone else's agenda. The whole point of the review is to start your week with proactive thinking about your own business, not someone else's problems.",
          },
          {
            label: "It signals to your brain that this is strategic, not chore",
            body:
              "Habits that feel like they have a purpose get done. Habits that feel like obligation get skipped. Framing this as 'how I start my week thinking strategically' makes it more likely you'll actually do it.",
          },
        ],
      },
      {
        type: "p",
        text:
          "Five minutes is the right budget. Long enough to extract a real signal, short enough that you won't dread it. Set a literal five-minute timer if you tend to rabbit-hole.",
      },
      { type: "h2", text: "The four numbers you'll check" },
      {
        type: "p",
        text:
          "Resist the urge to look at more than four. The skill is in restraint — picking the four that actually matter and ignoring the other 196 numbers GA4 will try to show you. These are the four:",
      },
      { type: "h3", text: "1. Active users — week over week" },
      {
        type: "p",
        text:
          "Where to find it: Reports → Engagement → Overview, top-left card. Set the date range to 'last week' and click Compare → 'previous period.' GA4 shows you the change as a percentage.",
      },
      {
        type: "p",
        text:
          "What you're asking: Did the same number of humans show up this week as last week? A 5-10% swing in either direction is noise — don't act on it. A 20%+ swing in either direction is signal — write down what it might be (a campaign, a holiday, a Google update, a product launch).",
      },
      { type: "h3", text: "2. Key events — week over week" },
      {
        type: "p",
        text:
          "Where to find it: Reports → Engagement → Events, sorted by event count descending, filtered to just your starred (key) events.",
      },
      {
        type: "p",
        text:
          "What you're asking: Did the things I care about (form submissions, sign-ups, purchases, primary CTA clicks) actually happen at the same rate? This is the number that matters more than traffic. A site that quadruples its traffic but holds key events flat has more of a problem than a site that loses 20% of its traffic but holds key events steady.",
      },
      {
        type: "p",
        text:
          "If you haven't set up key events yet — that's your homework before next Monday. The 'GA4 setup' guide on this blog walks you through it in 10 minutes.",
      },
      { type: "h3", text: "3. Channel distribution — last 7 days" },
      {
        type: "p",
        text:
          "Where to find it: Reports → Acquisition → Traffic acquisition, last 7 days.",
      },
      {
        type: "p",
        text:
          "What you're asking: Where did my traffic come from this week, and is one channel doing something unusual? Look at the top three channels. If your normal distribution is 60% Organic Search / 20% Direct / 20% Social and this week it's 40% / 20% / 40%, something happened in the Social column you should investigate. A shift in distribution is often a more useful early warning than a shift in the total.",
      },
      { type: "h3", text: "4. Top landing pages — last 7 days" },
      {
        type: "p",
        text:
          "Where to find it: Reports → Engagement → Landing page, sorted by sessions descending.",
      },
      {
        type: "p",
        text:
          "What you're asking: Where are people actually arriving on my site? It is almost never your homepage. If 60% of visitors land on one specific blog post or service page, that's your real 'front door' and where any conversion-rate optimization effort should focus. If a brand-new page appears in the top 5 unexpectedly, you've either gotten a backlink, ranked for something, or someone shared the link in a place worth investigating.",
      },
      { type: "h2", text: "The three questions to answer in writing" },
      {
        type: "p",
        text:
          "Five-minute reviews don't work unless you make them produce something. Open a Google Doc, a Notion page, or a paper notebook — anywhere you can keep a running log. For each Monday review, jot down three sentences. That's it. No paragraphs, no formatting.",
      },
      {
        type: "ul",
        items: [
          {
            label: "What changed this week?",
            body:
              "One sentence. 'Active users dropped 15%, mostly from Organic Search.' Or 'Conversions were flat but sessions grew 12%, so conversion rate dipped.' Or 'Nothing meaningful, all four numbers within 5% of last week.' Boring weeks are the most common; don't manufacture drama.",
          },
          {
            label: "Why do I think it changed?",
            body:
              "One sentence. Your best guess, written down before you look at any other data. 'Likely the Memorial Day holiday' or 'Probably the algorithm update I saw mentioned on Twitter' or 'I genuinely don't know yet.' Writing 'I don't know' is fine and useful — it tells future-you that the trail was cold.",
          },
          {
            label: "What's the one thing I'll try this week?",
            body:
              "One sentence. ONE thing. Not a strategy, not a five-point plan. 'I'll audit the top-trafficked blog post that's converting at 0.4% and add a clearer CTA.' Or 'I'll check Search Console to see which queries dropped.' Or 'Nothing — last week was normal, focus on shipping the new feature.' The discipline is the constraint to one. Two things means zero things will actually get done.",
          },
        ],
      },
      {
        type: "p",
        text:
          "After six weeks of doing this, you'll have a running log that tells you more about your business than any dashboard ever will. It captures the story of your traffic, your guesses about why, and what you actually did about it. That's the artifact most founders never produce, and the reason they feel like they can't get a grip on their data.",
      },
      { type: "h2", text: "What to skip during the 5-minute review" },
      {
        type: "p",
        text:
          "Discipline matters here as much as effort. Five minutes means five minutes. These are the things you'll be tempted to check but should not:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Real-time",
            body:
              "Useless for weekly decisions. Sample size is too small. Resist.",
          },
          {
            label: "Demographic breakdowns",
            body:
              "Interesting once a quarter, not weekly. If your audience suddenly changed in one week, something else will surface it.",
          },
          {
            label: "Device category breakdown",
            body:
              "Same. Mostly stable week-over-week. Worth a quarterly look, not weekly.",
          },
          {
            label: "Custom funnel reports",
            body:
              "Powerful but slow to build. Save for monthly deep-dives, not the 5-minute habit.",
          },
          {
            label: "Comparing the same week last year",
            body:
              "Year-over-year is a quarterly metric. Comparing this week to the same week last year on a weekly cadence introduces too many confounders (different products, different team size, different market). Stick to week-over-week.",
          },
        ],
      },
      { type: "h2", text: "What to do once a month (not weekly)" },
      {
        type: "p",
        text:
          "There ARE things worth checking at a slower cadence. Block a 30-minute slot on the first Monday of each month for these:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Top 10 landing pages and their conversion rate",
            body:
              "Identify your worst-performing high-traffic page. That's your highest-leverage optimization target for the month.",
          },
          {
            label: "New vs returning user trend",
            body:
              "If your business depends on repeat visitors (content sites, SaaS, marketplaces), this ratio over months tells you whether your retention work is paying off.",
          },
          {
            label: "Search Console queries that drove traffic",
            body:
              "Look for queries you're ranking for that you didn't expect — those often signal an unmet need or an unconverted intent.",
          },
          {
            label: "Year-over-year same month",
            body:
              "The honest answer to 'is the business growing or stagnant?' Doesn't appear in weekly numbers.",
          },
          {
            label: "Bounce/engagement rate on the top traffic page",
            body:
              "If engagement on the biggest page is dropping, every other number is at risk.",
          },
        ],
      },
      {
        type: "quote",
        text:
          "Five minutes, four numbers, three sentences, one decision. That's the entire system. The reason it works is that nothing about it scales to fill more time.",
      },
      { type: "h2", text: "The printable checklist" },
      {
        type: "p",
        text:
          "Print this, stick it on your monitor, or copy it into your weekly review template:",
      },
      {
        type: "ul",
        items: [
          { label: "Open GA4 → Reports → Engagement → Overview", body: "Compare last week vs previous week. Note Active users delta." },
          { label: "Switch to Events, filter by key events", body: "Note the key events count and the change." },
          { label: "Switch to Acquisition → Traffic acquisition", body: "Note any channel that shifted >10% of total share." },
          { label: "Switch to Engagement → Landing page", body: "Note any new page appearing in top 5." },
          { label: "Write three sentences", body: "What changed. Why I think so. The one thing I'll try this week." },
        ],
      },
      { type: "h2", text: "Or, let Plainly write the three sentences for you" },
      {
        type: "p",
        text:
          "If you'd rather not remember to do this every week — or if you've tried weekly habits before and they slipped — Plainly is what happens when you point this entire routine at automation. Connect your GA4 once, set a default property, and every Monday morning you wake up to an email with the four numbers, the change percentages, a written 'what changed and why,' and a one-thing-to-try-this-week recommendation. The free demo on the homepage shows you exactly the format, no signup needed.",
      },
      {
        type: "p",
        text:
          "Either way, the worst thing you can do is keep avoiding the data. Five minutes a week beats two hours a quarter beats nothing at all. Pick a path and start.",
      },
      { type: "h2", text: "A worked example: what a real review looks like" },
      {
        type: "p",
        text:
          "Theory is easy; concrete examples stick. Here's an actual five-minute review I ran on a client's site two weeks ago, formatted exactly the way I described above. Names changed but numbers are real.",
      },
      {
        type: "p",
        text:
          "Step 1: Active users last week were 2,847 vs 3,210 the week before. Down 11.3%. Worth noticing but not panic-worthy yet.",
      },
      {
        type: "p",
        text:
          "Step 2: Key events (in this case, contact_form_submitted and book_a_call_clicked combined) were 18 last week vs 22 the week before. Down 18%. Closer to last week's traffic drop than I'd hope, but conversion rate held — sessions producing key events at roughly the same proportion.",
      },
      {
        type: "p",
        text:
          "Step 3: Channel distribution this week — 52% Organic Search, 28% Direct, 12% Organic Social, 8% Email. Compared to typical 45/25/18/12, Organic Social was way down. Last week was 65 sessions vs 240 the prior week. Big drop in one channel.",
      },
      {
        type: "p",
        text:
          "Step 4: Top landing pages — the usual top three (homepage, the main service page, a popular blog post) were all stable. One previously-top-five page (an old blog post) had dropped to position 12. Otherwise stable.",
      },
      {
        type: "p",
        text:
          "Three sentences I wrote: 'Active users down 11%, primarily from Organic Social dropping ~70%. Most likely cause: the client's main Instagram poster was on vacation last week — three planned posts didn't go out. Plan: re-shoot the missed content this Friday, post it Saturday, see if next week recovers.'",
      },
      {
        type: "p",
        text:
          "Total time: 4 minutes 12 seconds. Hypothesis testable next week with zero ambiguity. No dashboards to design, no charts to build, no analyst to hire. That's the entire system in practice.",
      },
      { type: "h2", text: "Why most analytics habits fail" },
      {
        type: "p",
        text:
          "If you've tried this before and it didn't stick, you're not alone. The four most common failure modes:",
      },
      {
        type: "ul",
        items: [
          {
            label: "The 'every day' trap",
            body:
              "Daily checks generate too much noise — daily variance is huge, and you end up making bad decisions from random fluctuations. Weekly is the right cadence for almost all small businesses. If you're tempted to look more often, you're treating analytics like Twitter, not a strategic tool.",
          },
          {
            label: "The 'I'll do it when I have time' trap",
            body:
              "You will never have time. Schedule it. Put it on Monday at 8:30am as a recurring calendar event. Treat it like a meeting with yourself. The people who maintain weekly analytics habits long-term are universally the ones who scheduled it; the people who said 'I'll fit it in' universally dropped it within six weeks.",
          },
          {
            label: "The 'no output' trap",
            body:
              "If you don't write the three sentences down, nothing compounds. Six weeks from now, you won't remember what you concluded about that traffic dip in week 2. A running log is what turns the habit from 'checking numbers' into 'understanding your business over time.' Without it, you're just glancing.",
          },
          {
            label: "The 'perfect data' trap",
            body:
              "Some weeks the data is messy — your tracking broke, your default channel grouping changed, a campaign launched mid-week. You'll be tempted to skip the review until the data is clean. Don't. Write 'Data was messy this week because X. Next week's review will be clean.' That's still a useful log entry.",
          },
        ],
      },
    ],
  },
  {
    slug: "ga4-attribution-explained",
    title: "GA4 attribution explained: which channel actually drove the conversion?",
    description:
      "If your Google Ads and GA4 numbers don't match, or you're confused why 'Direct' makes up 40% of your traffic, attribution is the reason. Here's a plain-English explanation of how GA4 assigns credit, why it gets weird, and how to read the numbers honestly.",
    date: "2026-03-29",
    readMinutes: 11,
    keywords: [
      "GA4 attribution explained",
      "Google Analytics attribution model",
      "data-driven attribution GA4",
      "GA4 last click attribution",
      "why GA4 shows direct traffic",
      "GA4 vs Google Ads numbers",
      "marketing attribution",
    ],
    author: { name: "Nicholas Steer", role: "Founder, Plainly" },
    content: [
      {
        type: "p",
        text:
          "Attribution is the most mathematically slippery and emotionally exhausting concept in web analytics. It is also, weirdly, the place where most marketers spend the least time understanding the basics. They ship campaigns, watch the GA4 numbers do something confusing, and assume either GA4 or Google Ads is broken. Usually neither is. Usually it's attribution.",
      },
      {
        type: "p",
        text:
          "Here is the plain-English version of how it works, why your numbers feel wrong, and how to read them in a way that's actually useful for decisions. No PhD math. No buzzwords. Real examples.",
      },
      { type: "h2", text: "What attribution actually is" },
      {
        type: "p",
        text:
          "Attribution is the rule GA4 uses to decide which traffic source gets credit for a conversion when a customer touched your site multiple times before converting. That's it. The entire field of attribution is one question: who do we thank for the sale?",
      },
      {
        type: "p",
        text:
          "Most conversions don't happen on the first visit. A typical small-business customer journey looks like: sees a Facebook ad, doesn't click. Sees a follow-up retargeting ad three days later, clicks, browses, leaves. Searches your brand name on Google a week later, lands on your homepage. Types your URL directly two days after that and finally buys.",
      },
      {
        type: "p",
        text:
          "That's four touches across four different attribution sources (Paid Social, Paid Social again, Organic Search, Direct). Only one of them gets credit for the conversion in your reporting. Which one? It depends on which attribution model you're using.",
      },
      { type: "h2", text: "The 5 attribution models you need to know" },
      { type: "h3", text: "1. Last click" },
      {
        type: "p",
        text:
          "Whichever traffic source brought the user in for their final, converting visit gets 100% of the credit. In our example above, Direct wins. This is the simplest model and what GA4 used to default to before 2023. It's also extremely misleading because it ignores everything that built awareness and intent leading up to the final visit.",
      },
      { type: "h3", text: "2. First click" },
      {
        type: "p",
        text:
          "Whichever traffic source brought the user to your site for the very first time gets 100% of the credit. In our example, the first Paid Social touch wins. Better for understanding what builds initial awareness. Worse for understanding what closes the sale.",
      },
      { type: "h3", text: "3. Linear (equal credit)" },
      {
        type: "p",
        text:
          "Every touch in the customer journey gets equal credit. In our four-touch example, each source gets 25% of the conversion. Fair, but doesn't reflect reality — the first touch and the last touch usually matter more than the middle ones.",
      },
      { type: "h3", text: "4. Time decay" },
      {
        type: "p",
        text:
          "Recent touches get more credit than older ones, on an exponential curve. The Direct visit just before the conversion might get 50%, the Organic Search a week earlier 25%, the two Paid Social touches splitting the remaining 25%. Closer to reality for high-consideration purchases.",
      },
      { type: "h3", text: "5. Data-driven attribution (GA4's default since 2023)" },
      {
        type: "p",
        text:
          "GA4 uses machine learning to model what would have happened if a particular touch hadn't existed. If removing the Paid Social touch from the simulation drops the conversion probability by 40%, Paid Social gets 40% credit. This is statistically the most defensible model, but the math is opaque — you can't easily explain to a CFO why a particular channel got the credit it did.",
      },
      {
        type: "p",
        text:
          "Important context: data-driven attribution requires a minimum of 300 conversions and 3,000 interactions per channel per month for GA4 to have enough data. Most small businesses don't hit that bar, in which case GA4 quietly falls back to last-click without telling you. If you don't have data-driven attribution running, GA4 is essentially doing last-click anyway.",
      },
      { type: "h2", text: "Why your GA4 and Google Ads numbers don't match" },
      {
        type: "p",
        text:
          "This is the #1 attribution-related support ticket every analytics consultant gets. You launch a Google Ads campaign. Google Ads says you got 100 conversions. GA4 says you got 35. WHAT.",
      },
      {
        type: "p",
        text:
          "The reason: Google Ads and GA4 attribute conversions completely differently.",
      },
      {
        type: "ul",
        items: [
          {
            label: "Google Ads uses last-click within its own ecosystem",
            body:
              "If the user clicked any Google Ads ad within 30 days of conversion, Ads claims credit, even if the user came back via a completely different channel for the final visit. Ads has every motivation to over-claim.",
          },
          {
            label: "GA4 uses cross-channel data-driven (or last-click)",
            body:
              "GA4 looks at the entire journey including non-Google sources. If the final touch was actually Direct or Organic, that's who gets credit in GA4 — even if a Google ad was clicked earlier in the journey.",
          },
          {
            label: "Different conversion windows",
            body:
              "Google Ads defaults to a 30-day click attribution window. GA4 defaults can vary. Different windows = different numbers.",
          },
          {
            label: "View-through conversions",
            body:
              "Google Ads counts conversions where the user SAW your ad but didn't click. GA4 (and most other tools) only count clicks. This alone causes 20-40% gaps.",
          },
        ],
      },
      {
        type: "p",
        text:
          "There's no 'right' number. Both are showing you different views of the same journey. The most useful thing you can do is pick ONE source of truth for any decision — and for most small businesses, that should be GA4, because it sees the full cross-channel picture.",
      },
      { type: "h2", text: "Why 'Direct' is 40% of your traffic" },
      {
        type: "p",
        text:
          "If you look at GA4 → Acquisition → Traffic acquisition and 'Direct' is the biggest channel — bigger than Organic Search, bigger than your paid campaigns — you have what's known as a 'dark traffic' problem. Direct in GA4 means 'GA4 couldn't figure out where this visitor came from.' That's it. Some genuine direct traffic exists (people typing your URL or using bookmarks), but it's usually 5-15% of total. If you're seeing 40%+, something is misattributing.",
      },
      {
        type: "p",
        text:
          "The most common causes:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Email and SMS links without UTM parameters",
            body:
              "When someone clicks a link in your newsletter, that traffic gets bucketed as 'Direct' unless you tagged the link with ?utm_source=email or similar. Same with SMS, transactional emails, and in-app links.",
          },
          {
            label: "Mobile apps that strip the referrer",
            body:
              "iOS Safari, Instagram, TikTok, and Twitter mobile apps often don't pass the referring URL when users tap an external link. GA4 sees a referrer-less visit and labels it Direct.",
          },
          {
            label: "HTTPS-to-HTTP redirects",
            body:
              "If your site uses HTTP anywhere in the chain (it shouldn't, but it happens), browsers strip the referrer header. GA4 then labels the visit Direct. Make sure you're 100% HTTPS site-wide.",
          },
          {
            label: "Privacy-conscious users and ad blockers",
            body:
              "Some users actively block referrer headers. That traffic shows up as Direct. There's nothing you can do about this except be aware it inflates your Direct numbers.",
          },
        ],
      },
      { type: "h2", text: "How to actually use attribution data" },
      {
        type: "p",
        text:
          "Three practical rules for not losing your mind:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Pick one model, stick with it, and only compare apples to apples",
            body:
              "Don't switch between attribution models when comparing periods. If you're tracking month-over-month using data-driven, do the whole comparison in data-driven. Switching models will produce 'changes' that are pure measurement artifacts.",
          },
          {
            label: "Use UTM parameters religiously on EVERYTHING you send out",
            body:
              "Every email link, every social post, every paid ad, every QR code, every podcast sponsor link — UTM-tag it. This single discipline reduces Direct traffic to its true 5-15% level and makes attribution dramatically more accurate.",
          },
          {
            label: "Run the 'channel-off' thought experiment",
            body:
              "Imagine you killed your Facebook ads tomorrow. Would total conversions drop by 30% (Facebook is doing real work) or by 3% (Facebook is mostly catching people who would have converted anyway)? Last-click won't answer this. Data-driven attribution will. If you don't have data-driven running, run small holdouts — pause one channel for two weeks and see what happens to total conversions.",
          },
        ],
      },
      { type: "h2", text: "What to do when attribution makes a decision unclear" },
      {
        type: "p",
        text:
          "Attribution will sometimes give you contradictory signals. Last-click says channel A is winning; first-click says channel B; data-driven says they're roughly tied. When that happens, default to incrementality testing — temporarily pause a channel and measure the actual change in conversions. It's slower and costlier than analytics, but it's the only honest test of whether a marketing dollar drives real revenue.",
      },
      {
        type: "p",
        text:
          "For most small businesses, the practical rule is: use last-click for tactical (campaign-level) decisions, use data-driven for strategic (channel mix) decisions, and use incrementality testing when the answer really matters.",
      },
      {
        type: "quote",
        text:
          "Attribution isn't math you solve once and forget. It's a lens you choose deliberately for each decision. The mistake is forgetting which lens you picked.",
      },
      { type: "h2", text: "How Plainly handles attribution" },
      {
        type: "p",
        text:
          "Plainly reads whatever attribution model your GA4 property is configured with — we don't override Google's choice. What we do is translate the attribution-driven numbers into plain English: 'Sessions from Organic Search dropped 18% week-over-week, which directly explains why total conversions fell 12%.' Connections like that are easy to lose in a dashboard and easy to surface in a written summary. If you've ever stared at GA4 wondering 'why the heck did conversions drop?' — that's exactly the question Plainly is designed to answer in two paragraphs. The free demo on the homepage shows you exactly the format.",
      },
    ],
  },
  {
    slug: "how-to-set-up-ga4-conversions-key-events",
    title: "How to set up GA4 conversions (key events) in 10 minutes — without breaking your tag",
    description:
      "GA4 renamed 'conversions' to 'key events' and the setup is confusing. Here's the cleanest 10-minute path to track form submissions, button clicks, and purchases — with a working example for WordPress, Shopify, and custom sites.",
    date: "2026-03-22",
    readMinutes: 9,
    keywords: [
      "GA4 conversions setup",
      "GA4 key events tutorial",
      "track form submission GA4",
      "set up conversions Google Analytics 4",
      "GA4 button click tracking",
      "GA4 conversion not tracking",
      "key events vs conversions GA4",
    ],
    author: { name: "Nicholas Steer", role: "Founder, Plainly" },
    content: [
      {
        type: "p",
        text:
          "Setting up GA4 conversions — now called 'key events' — is the single most important thing you can do after installing GA4, and the thing that 70% of people skip. Without key events, GA4 is measuring how many people visited but has no idea whether your site is actually working for your business. Traffic without conversions is decoration; it doesn't tell you anything actionable.",
      },
      {
        type: "p",
        text:
          "Good news: setting up the four conversions that matter for most small businesses takes about 10 minutes if you know the cleanest path. Bad news: GA4's documentation makes this look harder than it actually is, and most online tutorials are written for the previous version of GA4 before Google renamed things. Here's the current, clean version.",
      },
      { type: "h2", text: "First: what changed and what to call it" },
      {
        type: "p",
        text:
          "In late 2024, Google renamed 'conversions' to 'key events' in GA4. The functionality is the same. The terminology change matters because tutorials referencing 'conversions' as a separate concept are outdated. Here's the current vocabulary:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Event",
            body:
              "Any action GA4 records — page view, scroll, click, form submission, anything. GA4 tracks dozens of events automatically; you create custom ones for the things specific to your business.",
          },
          {
            label: "Key event",
            body:
              "An event you've manually marked as 'this is important.' Key events flow into your conversion reports and into Google Ads bid optimization. An event is just data; a key event is data with intent.",
          },
          {
            label: "Conversion",
            body:
              "Now an alias for 'key event' in most contexts. You'll still see the word 'conversion' in some Google Ads UIs, but in GA4, the term is officially 'key event' going forward.",
          },
        ],
      },
      { type: "h2", text: "The four key events most businesses need" },
      {
        type: "p",
        text:
          "Resist setting up 20 key events on day one. You won't track them all, and you'll confuse Google Ads' bid optimization. Pick four. The right four for most businesses:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Form submitted",
            body:
              "Contact form, demo request, newsletter signup — whichever is your primary lead-capture form. Track when the form actually submits successfully, not when the submit button is clicked.",
          },
          {
            label: "Primary CTA clicked",
            body:
              "Whichever button you most want people to click — 'Buy now', 'Start free trial', 'Book a call.' Tracks intent even if they don't complete the next step.",
          },
          {
            label: "Pricing page viewed",
            body:
              "Anyone who views your pricing page is showing high intent. Treating this as a key event lets you compare 'people who viewed pricing' against 'people who actually converted' to calculate pricing-page conversion rate.",
          },
          {
            label: "Purchase (e-commerce only)",
            body:
              "If you sell anything online. Tracked with monetary value so GA4 can also show revenue alongside conversion count.",
          },
        ],
      },
      { type: "h2", text: "Setup path #1: WordPress (5 minutes)" },
      {
        type: "p",
        text:
          "Easiest path. Use the official Google Site Kit plugin combined with your form plugin's GA integration.",
      },
      {
        type: "ul",
        items: [
          {
            label: "Install Site Kit by Google",
            body:
              "From WordPress admin → Plugins → Add New, search 'Site Kit by Google.' Install and activate. Connect your Google account. This handles base GA4 installation and the page_view event automatically.",
          },
          {
            label: "Form submissions: use your form plugin's GA add-on",
            body:
              "Gravity Forms, WPForms, Contact Form 7, and Ninja Forms all have either built-in GA event tracking or a free add-on. Set the event name to form_submit_contact (or your preferred name). The plugin fires the event on successful submission.",
          },
          {
            label: "Pricing page view: use GA4's built-in custom event",
            body:
              "In GA4: Admin → Events → Create event. Match the existing page_view event with parameter page_location containing /pricing. Save as viewed_pricing_page.",
          },
          {
            label: "Primary CTA click: enable Enhanced Measurement outbound clicks",
            body:
              "GA4 already tracks outbound clicks automatically. For internal buttons, the easiest path is to wrap your button in an HTML link with a tracked ID like /pricing#cta-primary-clicked and use the page_view trick above.",
          },
          {
            label: "Mark as key events",
            body:
              "In GA4: Admin → Events → toggle 'Mark as key event' for each of the events you created. Without this toggle, the events track but aren't surfaced in conversion reports.",
          },
        ],
      },
      { type: "h2", text: "Setup path #2: Shopify (3 minutes)" },
      {
        type: "p",
        text:
          "Shopify auto-tracks the big ones if you've connected GA4. From Shopify admin → Online Store → Preferences → Google Analytics, paste your Measurement ID. Shopify automatically fires purchase, add_to_cart, begin_checkout, and view_item events.",
      },
      {
        type: "p",
        text:
          "To mark them as key events, go to GA4: Admin → Events. You'll see purchase, add_to_cart, begin_checkout in the list. Toggle 'Mark as key event' for purchase only. Don't mark add_to_cart and begin_checkout as key events — they happen for non-buyers too, and treating them as conversions inflates your conversion rate.",
      },
      {
        type: "p",
        text:
          "For form submissions on Shopify content pages (contact forms, newsletter signups), use the Shopify Customer Events feature: Admin → Settings → Customer events → Add custom pixel. Fire a gtag('event', 'form_submit_contact') call on form submission.",
      },
      { type: "h2", text: "Setup path #3: Custom site with Google Tag Manager (10 minutes)" },
      {
        type: "p",
        text:
          "Most flexible path, recommended if you're not on a platform with built-in GA integration.",
      },
      {
        type: "ul",
        items: [
          {
            label: "Step 1: Install GTM if you haven't",
            body:
              "Get a container at tagmanager.google.com. Paste the GTM snippet in your site's <head> and right after the opening <body> tag.",
          },
          {
            label: "Step 2: Add a 'Google tag' (GA4 base) tag in GTM",
            body:
              "New tag → Tag type 'Google Tag' → enter your Measurement ID → trigger on All Pages → publish. This fires GA4 page_view on every page automatically.",
          },
          {
            label: "Step 3: Form submissions via a Form Submission trigger",
            body:
              "New trigger → Trigger type 'Form Submission' → trigger on specific Form ID or URL contains '/contact'. New tag → Tag type 'GA4 Event' → Event name 'form_submit_contact' → trigger on the form trigger. Test in GTM Preview mode.",
          },
          {
            label: "Step 4: Button click via a Click Listener trigger",
            body:
              "New trigger → Trigger type 'Click - All Elements' → fire on Click Classes matches CSS Selector .cta-primary (or whatever your button class is). New tag → 'GA4 Event' → Event name 'primary_cta_clicked' → trigger on the click trigger.",
          },
          {
            label: "Step 5: Pricing page view via GA4's existing event",
            body:
              "Same as the WordPress path: in GA4 directly, Admin → Events → Create event → match page_view + page_location contains /pricing → name viewed_pricing_page.",
          },
          {
            label: "Step 6: Mark all four as key events",
            body:
              "GA4: Admin → Events → toggle 'Mark as key event' for each. Wait 24 hours for the first conversion reports to populate.",
          },
        ],
      },
      { type: "h2", text: "Verify they're actually firing" },
      {
        type: "p",
        text:
          "Don't trust the dashboards yet. Verify each event individually:",
      },
      {
        type: "ul",
        items: [
          { label: "Open your site in incognito", body: "Avoids login/cache confusion." },
          { label: "Open GA4 → Reports → Realtime → Events", body: "Live event stream." },
          { label: "Trigger each event by performing the action", body: "Submit a test form, click the button, view the pricing page." },
          { label: "Watch the event appear in Realtime within ~30 seconds", body: "If it doesn't, your tag isn't firing. Use GTM Preview mode or browser console to debug." },
        ],
      },
      { type: "h2", text: "Common mistakes that silently break tracking" },
      {
        type: "ul",
        items: [
          {
            label: "Firing form_submit on button click instead of form success",
            body:
              "If the user clicks Submit but the form has a validation error, you'll count it as a conversion. Trigger on actual successful submission only — typically by hooking into the form library's success callback.",
          },
          {
            label: "Forgetting to mark events as key events",
            body:
              "The events flow in but don't appear in conversion reports. Easy fix; just toggle them.",
          },
          {
            label: "Duplicate events from two tag sources",
            body:
              "If you have both Site Kit AND a manual GTM install, every event fires twice. Realtime will show 2x your actual traffic. Pick one source and remove the other.",
          },
          {
            label: "Missing the 24-hour processing delay",
            body:
              "GA4 takes 24-48 hours to populate non-Realtime conversion reports. If you check the standard Engagement reports the day after setup and see zero conversions, that doesn't mean tracking failed. Wait 48 hours and re-check.",
          },
          {
            label: "Re-using event names across different actions",
            body:
              "Don't call your contact form submission 'submit' and your newsletter submission 'submit' both. Pick distinct names like form_submit_contact and form_submit_newsletter. Otherwise you can't tell which is which in reports.",
          },
        ],
      },
      { type: "h2", text: "After setup: don't go event-crazy" },
      {
        type: "p",
        text:
          "Once you've nailed the four basics, resist the temptation to add 15 more events. Each additional event dilutes Google Ads' optimization signal (it has less data per event to learn from) and clutters your reports. Add a fifth or sixth event only when you have a specific question that requires it — like 'how many users watch the demo video?' Don't add events because they're easy to add.",
      },
      {
        type: "quote",
        text:
          "Tracking is the boring half of analytics. Without it, the fun half (insight) is built on sand. Spend the 10 minutes; you'll get them back a hundred times.",
      },
      { type: "h2", text: "What happens after key events are firing" },
      {
        type: "p",
        text:
          "Once your four key events have been live for a week or two, you finally have something worth analyzing. You can answer real questions: 'What's my pricing-page-to-purchase conversion rate?' 'Which channel drives the most form submissions per session?' 'Is the new homepage CTA design beating the old one?' All of those questions are impossible without key events; all become trivial once they're set up. If you want those questions answered automatically every Monday morning in plain English instead of digging through reports, that's exactly what Plainly was built to do — connect once, get a weekly read-out, free demo on the homepage.",
      },
    ],
  },
  {
    slug: "ga4-metrics-that-actually-matter",
    title: "The 7 GA4 metrics that actually matter (and the 5 that don't)",
    description:
      "The seven GA4 metrics that actually matter for a small business are: sessions, users, conversions (key events), conversion rate, engagement rate, session source/medium, and average session duration. The five to ignore are pageviews, exit rate, percent new sessions, raw bounce rate, and screen views — each either measures something more useful elsewhere or distorts decisions.",
    date: "2026-03-15",
    readMinutes: 9,
    keywords: [
      "GA4 metrics explained",
      "important GA4 metrics",
      "what metrics matter in Google Analytics",
      "GA4 bounce rate",
      "GA4 engagement rate",
      "GA4 sessions vs users",
      "key events GA4",
    ],
    author: { name: "Nicholas Steer", role: "Founder, Plainly" },
    content: [
      {
        type: "p",
        text:
          "Open GA4 and you can choose from roughly 200 metrics across the various reports. Some are essential. Some are vanity. Some are quietly misleading. The actual problem most small businesses face isn't 'we don't have enough data' — it's 'we don't know which 12 numbers to focus on and which 188 to ignore.'",
      },
      {
        type: "p",
        text:
          "This is the short, opinionated list. Seven metrics that genuinely matter for the average business, five that almost nobody should pay attention to, and a quick note on the ones that are conditionally useful. Each one is explained in plain English with what it actually tells you about your business — not what GA4's tooltip says.",
      },
      { type: "h2", text: "The 7 metrics that matter" },
      { type: "h3", text: "1. Active users" },
      {
        type: "p",
        text:
          "What it is: the count of unique humans (well, unique browsers) who interacted with your site in the time period you're looking at. \"Active\" in GA4 means they did at least one of: triggered a key event, viewed a second page, or stayed at least 10 seconds.",
      },
      {
        type: "p",
        text:
          "Why it matters: this is the closest thing GA4 has to \"how many real people came to my site this week.\" It's the headline number for understanding whether your audience is growing.",
      },
      {
        type: "p",
        text:
          "The gotcha: GA4 also shows you \"Total users\" and \"New users\" and a few other flavors. They are not the same number. Pick \"Active users\" as your standard and stop comparing across the others — that's where almost everyone gets confused.",
      },
      { type: "h3", text: "2. Sessions" },
      {
        type: "p",
        text:
          "What it is: a session is one continuous visit. One person who comes back three times in a week generates three sessions but one user.",
      },
      {
        type: "p",
        text:
          "Why it matters: when you compare sessions to users, the ratio tells you something. Sessions/Users above 1.3 means people are coming back; close to 1.0 means most visitors come once and leave. For a blog or content site that ratio should be 1.5+; for a one-time-purchase site, 1.1 is fine.",
      },
      { type: "h3", text: "3. Engaged sessions and engagement rate" },
      {
        type: "p",
        text:
          "What it is: an \"engaged session\" is one where the visitor stayed at least 10 seconds, viewed a second page, or triggered a key event. Engagement rate is the percentage of sessions that meet that bar.",
      },
      {
        type: "p",
        text:
          "Why it matters: this is the metric that replaced \"bounce rate\" in GA4, and it's better. A 65% engagement rate means 65% of visits had at least minimal interest; below 40% suggests either bad traffic sources, slow load times, or wrong-fit content. Use this — not bounce rate — to evaluate whether your visitors care about your site.",
      },
      { type: "h3", text: "4. Key events (the new name for \"conversions\")" },
      {
        type: "p",
        text:
          "What it is: any specific action you've told GA4 to count as important — form submissions, button clicks, file downloads, purchases, video plays. You configure these manually under Admin → Events.",
      },
      {
        type: "p",
        text:
          "Why it matters: this is the only metric on the page that has anything to do with whether your website is actually helping your business. Sessions and engagement are inputs; key events are outputs. If you have not set up at least one key event in GA4, the whole tool is just telling you about traffic that may or may not be doing anything for you.",
      },
      {
        type: "p",
        text:
          "Setup tip: at minimum, configure key events for \"contact form submitted\" and \"primary call-to-action clicked\" within your first week. Without these, GA4 is purely vanity tracking.",
      },
      { type: "h3", text: "5. Sessions by Default Channel Group (in Acquisition)" },
      {
        type: "p",
        text:
          "What it is: a breakdown of where your sessions come from — Organic Search, Direct, Email, Social, Paid Search, Referral, Organic Social, etc. Found in Reports → Acquisition → Traffic acquisition.",
      },
      {
        type: "p",
        text:
          "Why it matters: this is the single most actionable view in GA4. If 80% of your traffic comes from Organic Search and Google ships an algorithm update that hurts you, you have a real problem. If your traffic is balanced across 5 channels, you're more resilient. The distribution tells you the health of your business model, not just your website.",
      },
      { type: "h3", text: "6. Top landing pages (Reports → Engagement → Landing page)" },
      {
        type: "p",
        text:
          "What it is: the pages that are the first thing visitors see when they arrive at your site.",
      },
      {
        type: "p",
        text:
          "Why it matters: this is your site's actual \"front door,\" and it is almost never your homepage. For most content-driven sites, 60-80% of traffic lands on a blog post or a specific service page. Optimizing those pages — the one with conversion CTAs, signup forms, and clear next steps — moves the needle more than optimizing your homepage.",
      },
      { type: "h3", text: "7. Conversion rate (key events ÷ sessions)" },
      {
        type: "p",
        text:
          "What it is: of all the sessions, what percentage triggered your key event. GA4 doesn't show this directly as a single metric, but it's easy to calculate: divide key events by sessions for the same period.",
      },
      {
        type: "p",
        text:
          "Why it matters: conversion rate isolates whether your site converts visitors well, separate from how much traffic you're getting. A site that gets 100 visitors and converts 5 is healthier than a site that gets 10,000 visitors and converts 50. Same absolute number, vastly different business.",
      },
      {
        type: "p",
        text:
          "Benchmarks vary wildly by industry, but typical ranges: B2B lead-gen sites 2-5%, e-commerce 1-3%, content sites with newsletter signups 1-2%. If you're below those, fix the site before buying more traffic.",
      },
      { type: "h2", text: "The 5 metrics that don't matter (much)" },
      { type: "h3", text: "1. Bounce rate (the old one)" },
      {
        type: "p",
        text:
          "GA4 actually deprecated bounce rate from the default views, but you can still add it back. Don't. The GA4 bounce rate definition is \"100% minus engagement rate\" — meaning a high bounce rate on a blog post (where someone reads the whole article in one minute and leaves satisfied) is technically a \"bounce\" but isn't a problem at all.",
      },
      {
        type: "p",
        text:
          "Use engagement rate instead. It tells you the same story without the misleading framing.",
      },
      { type: "h3", text: "2. Average engagement time" },
      {
        type: "p",
        text:
          "GA4 shows you the average engagement time per session, per page, etc. It sounds useful — \"are people reading my content?\" — but it's wildly distorted by outliers. One visitor who leaves a tab open for 4 hours will skew your average. The median would be informative; the mean isn't.",
      },
      {
        type: "p",
        text:
          "If you care about engagement, look at scroll depth (configure it as a key event) or specific page-completion events. Pure time-on-page is mostly noise.",
      },
      { type: "h3", text: "3. Real-time" },
      {
        type: "p",
        text:
          "Real-time is fun to watch — there are 12 people on the site right now, three of them are in California, look at them go. But the sample is too small to make any decision from. Real-time is good for confirming your tag fires and that's it. Close the tab.",
      },
      { type: "h3", text: "4. Total users vs. new users (separately)" },
      {
        type: "p",
        text:
          "GA4 lets you display these separately on dashboards. Don't. \"New users\" is roughly meaningless without context, and \"total users\" inflates over long periods in a way that doesn't reflect business reality. Stick to Active Users and you'll save yourself confusion.",
      },
      { type: "h3", text: "5. Average session duration" },
      {
        type: "p",
        text:
          "Same problem as average engagement time, but worse — GA4's session-duration calculation has known edge cases where short sessions get measured as zero. A high \"average session duration\" can simply mean you have outlier long sessions, not that your content is engaging. Ignore.",
      },
      { type: "h2", text: "Conditionally useful: metrics that matter if X" },
      {
        type: "p",
        text:
          "A few that aren't on either list because they only matter for specific business models:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Ecommerce revenue and transactions",
            body:
              "Critical if you sell online; ignore otherwise. Configure GA4 ecommerce tracking through GTM or your platform's plugin.",
          },
          {
            label: "Page load time / Core Web Vitals",
            body:
              "Matter for SEO because Google ranks faster sites higher. Found in Search Console, not GA4. Worth a monthly check.",
          },
          {
            label: "Device category",
            body:
              "Only matters if you discover something surprising. If mobile is 75% of your traffic and you've never tested the mobile experience, that's an emergency. Otherwise, low priority.",
          },
          {
            label: "Geography",
            body:
              "Useful if you're spending on geo-targeted ads. Otherwise, just curiosity.",
          },
          {
            label: "Search queries (via Search Console integration)",
            body:
              "Massively useful if you care about SEO. Connect Search Console to GA4 and you can see which queries drove your organic sessions.",
          },
        ],
      },
      { type: "h2", text: "The Monday-morning version" },
      {
        type: "p",
        text:
          "If you only have 60 seconds a week, look at exactly four things, in this order:",
      },
      {
        type: "ul",
        items: [
          { label: "Active users (week-over-week change)", body: "Are people showing up at the same rate as last week?" },
          { label: "Key events (week-over-week change)", body: "Are they doing what you want them to do?" },
          { label: "Top channel performance", body: "Did one specific source change significantly?" },
          { label: "Top landing page", body: "Where are the people you got actually arriving?" },
        ],
      },
      {
        type: "p",
        text:
          "Those four answers give you 90% of what you need to know about your website most weeks. Everything else is noise unless something specific catches your attention.",
      },
      {
        type: "quote",
        text:
          "GA4 measures everything. Your business cares about four things. The skill is in knowing which four — and ignoring the other 196.",
      },
      { type: "h2", text: "Try Plainly free — see your four numbers in plain English" },
      {
        type: "p",
        text:
          "What we just walked through is essentially the Plainly worldview: pull the few metrics that matter, compare them week-over-week, explain what the changes mean, suggest what to try next. The free demo on the homepage will show you exactly this kind of read-out for a sample dataset — no signup required, no card. If you like the format, you can connect your real GA in one click.",
      },
    ],
  },
  {
    slug: "how-to-connect-google-analytics-4-the-right-way",
    title: "How to connect Google Analytics 4 the right way (the 6-step setup that everyone gets wrong)",
    description:
      "Most GA4 setups are silently broken. Tracking codes fire but key events aren't configured, or admins forget to verify data is flowing. Here's the 6-step setup that gets you to a real, useful GA4 — in about 25 minutes.",
    date: "2026-03-08",
    readMinutes: 8,
    keywords: [
      "how to set up GA4",
      "GA4 setup guide",
      "install Google Analytics 4",
      "GA4 tracking code",
      "GA4 key events setup",
      "GA4 configuration",
      "Google Analytics 4 install",
    ],
    author: { name: "Nicholas Steer", role: "Founder, Plainly" },
    content: [
      {
        type: "p",
        text:
          "Most GA4 installations look fine on the surface and are quietly broken underneath. The tag fires, the dashboard shows numbers, everything seems OK — but key events aren't configured, the wrong domain is being tracked, or the install is double-counting visits. By the time you realize the data has been wrong for six months, you've already made decisions based on it.",
      },
      {
        type: "p",
        text:
          "This is the 6-step setup that actually gets you to a useful GA4 — one where the numbers you see are the numbers you should trust. It takes about 25 minutes if your site is on a normal platform (WordPress, Shopify, Webflow, Squarespace, or a custom-built site with GTM). We'll cover the steps in order so each one is verifiable before you move on.",
      },
      { type: "h2", text: "Before you start: gather these 3 things" },
      {
        type: "ul",
        items: [
          {
            label: "Admin access to your Google account",
            body:
              "Personal Gmail or a Google Workspace account that owns or has Edit access to whatever GA property already exists. If you've been using GA in the past you probably already have this.",
          },
          {
            label: "Admin access to your website",
            body:
              "WordPress admin login, Shopify owner, Webflow account holder, or wherever your site is hosted. You need the ability to add code to the site's <head>.",
          },
          {
            label: "A list of 2-3 things you actually want to track",
            body:
              "Write these down before you start. Examples: \"contact form submitted,\" \"newsletter signup,\" \"pricing page viewed,\" \"primary CTA clicked.\" If you can't list at least two specific events, you're going to set up GA and immediately have nothing meaningful to measure.",
          },
        ],
      },
      { type: "h2", text: "Step 1: Create the GA4 property (3 minutes)" },
      {
        type: "p",
        text:
          "Go to analytics.google.com and click Admin (the gear icon in the bottom left). Click \"+ Create\" → \"Property.\" Name your property after your business (\"Plainly\" not \"Plainly's GA4\"). Pick your reporting timezone — get this right; changing it later splits your historical data in a way that looks ugly forever.",
      },
      {
        type: "p",
        text:
          "Pick your reporting currency if you sell anything (it controls how revenue is displayed). Click through the business questions on the next screen — they don't actually affect anything, just give them honest answers.",
      },
      { type: "h2", text: "Step 2: Add a Web data stream (1 minute)" },
      {
        type: "p",
        text:
          "After creating the property, GA4 asks you what kind of data stream you want. Choose Web. Enter your full URL with https:// (e.g. https://myplainly.com) — get this exactly right. If you have both www and non-www versions of your site, set up redirects on your host so only one canonical URL is used, then enter that one here. Don't set up two streams.",
      },
      {
        type: "p",
        text:
          "Give the stream a descriptive name (\"myplainly.com Web\"), accept the Enhanced Measurement defaults (these automatically track scrolls, outbound clicks, downloads — almost always what you want), and click Create.",
      },
      {
        type: "p",
        text:
          "GA4 will now display your Measurement ID — a string like G-XXXXXXXXXX. Copy this; you'll paste it in step 3.",
      },
      { type: "h2", text: "Step 3: Install the tracking code (5 minutes)" },
      {
        type: "p",
        text:
          "This is the step everyone does and then assumes is done. It's not done until you verify it's done — see step 4. Installation depends on your platform:",
      },
      {
        type: "ul",
        items: [
          {
            label: "WordPress",
            body:
              "Install a plugin like Site Kit by Google (official) or GA4 Google Analytics. Site Kit will walk you through linking your Google account; the plugin handles tag insertion. Don't paste the gtag.js code manually if you're using a plugin — that creates duplicate tracking.",
          },
          {
            label: "Shopify",
            body:
              "Admin → Online Store → Preferences → Google Analytics → Enter your Measurement ID. Shopify handles the rest. If you have a Shopify theme that includes its own GA snippet, remove that — Shopify's built-in tag is enough.",
          },
          {
            label: "Webflow",
            body:
              "Project Settings → Integrations → Google Analytics → paste your Measurement ID. Webflow inserts the tag site-wide. Don't ALSO paste a custom code snippet.",
          },
          {
            label: "Squarespace",
            body:
              "Settings → Advanced → External API Keys → Google Analytics → paste your Measurement ID. Same warning about not doubling up.",
          },
          {
            label: "Custom site with Google Tag Manager",
            body:
              "GTM is the recommended path for any custom site. In GTM, create a new tag of type \"Google tag\" (formerly GA4 Configuration), set the trigger to \"All Pages,\" and publish. GTM is also where you'll set up custom events in step 5.",
          },
          {
            label: "Custom site without GTM",
            body:
              "Paste the gtag.js snippet GA4 gave you directly into the <head> of every page. Easiest with a shared layout file (HTML include, React shared layout, server-rendered template).",
          },
        ],
      },
      { type: "h2", text: "Step 4: Verify it's actually working (2 minutes)" },
      {
        type: "p",
        text:
          "This is the step nobody does and then everyone regrets. The fastest way to verify GA4 is firing:",
      },
      {
        type: "ul",
        items: [
          { label: "Open your site in an incognito tab", body: "So your installation can't get fooled by cached login state or extensions." },
          { label: "Open DevTools", body: "F12 on most browsers. Go to the Network tab." },
          {
            label: "Filter for \"collect\"",
            body:
              "Reload the page. You should see a request to https://www.google-analytics.com/g/collect... (or sometimes https://analytics.google.com/g/collect). If you see one — GA4 is firing. If you don't see one, your install is broken regardless of what GA4's dashboard says.",
          },
          {
            label: "Open GA4 → Reports → Realtime",
            body:
              "Within 30 seconds of loading your site, you should appear as 1 active user in the Realtime view. If not, something between your site and GA4 is blocking the request — ad blocker in your browser, server-side firewall, CSP, or a broken Measurement ID.",
          },
        ],
      },
      { type: "h2", text: "Step 5: Configure your key events (10 minutes)" },
      {
        type: "p",
        text:
          "This is where 90% of GA4 setups fall apart. Without key events configured, GA4 is just measuring traffic — not measuring whether anything good happens because of that traffic.",
      },
      {
        type: "p",
        text:
          "Go back to Admin → Events. You'll see automatic events GA4 tracks for free: page_view, session_start, first_visit, scroll, outbound clicks. These are useful but they're not unique to your business.",
      },
      {
        type: "p",
        text:
          "The events you write down in the pre-flight checklist are the ones to add now. The setup path depends on how you're tagging:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Form submissions via GTM",
            body:
              "Create a GTM tag of type \"GA4 Event\" with an event name like form_submit_contact. Trigger it on a Form Submission listener matching your form's ID or URL. Test in GTM's Preview mode before publishing.",
          },
          {
            label: "Form submissions on a custom site",
            body:
              "In your form's success handler, call gtag('event', 'form_submit_contact'). Push it after the form is actually submitted (not when the button is clicked, which could be a click-then-validation-error).",
          },
          {
            label: "Button or link clicks",
            body:
              "Either GTM with a Click Listener trigger filtered to specific selectors, or in custom code: gtag('event', 'cta_clicked_pricing') in your onClick handler.",
          },
          {
            label: "Page views of a specific URL",
            body:
              "Go to Admin → Events → Create Event. Match the event name page_view with parameter page_location containing the URL you care about. Save with a custom name like viewed_pricing_page.",
          },
        ],
      },
      {
        type: "p",
        text:
          "After you create each event, also mark it as a \"Key Event\" by toggling the switch in the Events table. Without that toggle, GA4 tracks the event but doesn't include it in conversion reports.",
      },
      {
        type: "p",
        text:
          "Trigger each event from your incognito tab and verify it appears in Reports → Realtime → Events. If it doesn't show up within a minute, the tag isn't firing — go back to GTM Preview or your console.log to debug before moving on.",
      },
      { type: "h2", text: "Step 6: Connect Search Console (3 minutes)" },
      {
        type: "p",
        text:
          "This step is technically optional but unlocks one of the most useful views in GA4: which Google search queries actually drove your traffic.",
      },
      {
        type: "p",
        text:
          "Admin → Property → Search Console Links → Link → choose the Search Console property for your domain (you should already have this set up; if not, go verify your site at search.google.com/search-console first). Pick your web data stream. Confirm.",
      },
      {
        type: "p",
        text:
          "Once linked, you'll see a new section in GA4: Reports → Acquisition → Google organic search traffic. This is where you can finally answer questions like \"which keywords are sending me organic traffic that converts?\" — without this link, GA4 only shows you \"organic search\" as one undifferentiated lump.",
      },
      { type: "h2", text: "The post-setup audit checklist" },
      {
        type: "p",
        text:
          "Wait 48 hours for data to populate, then verify each of these. If any fail, fix before relying on the data:",
      },
      {
        type: "ul",
        items: [
          { label: "Realtime shows users", body: "Sanity check that the tag is firing." },
          { label: "Reports → Engagement → Events shows your custom events", body: "If page_view is there but your form_submit_contact isn't, the custom event wiring is broken." },
          { label: "Reports → Acquisition shows multiple channels", body: "If 100% of your traffic is showing as \"Direct,\" your tag is misconfigured (very common with tag-blocking redirects)." },
          { label: "Cross-check against another source", body: "If your CRM logs 10 form submissions yesterday and GA4 shows 0 form_submit events, something is broken in the event wiring." },
          { label: "No internal traffic in your numbers", body: "Admin → Data Streams → click your stream → More Tagging Settings → Define Internal Traffic. Add your office IP so you don't pollute your own data." },
        ],
      },
      { type: "h2", text: "Common setup mistakes (in order of how often we see them)" },
      {
        type: "ul",
        items: [
          {
            label: "No key events configured",
            body:
              "By a mile, the most common mistake. People install the tag, see pretty numbers, and never set up the events that would actually tell them whether the site is working.",
          },
          {
            label: "Duplicate tags",
            body:
              "Site has GA4 installed via WordPress plugin AND a manually-pasted snippet. Result: every visit is counted twice. Look at Realtime and add yourself as 1 user; if you appear as 2, you have duplicates.",
          },
          {
            label: "Wrong canonical URL",
            body:
              "GA4 stream URL is www.myplainly.com but the site actually serves myplainly.com (no www). The tag still fires but Acquisition reports get weird.",
          },
          {
            label: "Internal traffic not filtered",
            body:
              "You and your team's daily visits become 30% of your data. Realtime numbers especially get polluted.",
          },
          {
            label: "Enhanced Measurement disabled",
            body:
              "Someone unchecked the box during setup. Now scroll depth, outbound clicks, file downloads, etc. aren't tracked. Re-enable in Admin → Data Streams → your stream → Enhanced Measurement.",
          },
        ],
      },
      {
        type: "quote",
        text:
          "A GA4 setup that 'looks fine' usually isn't. Always verify with the Network tab and Realtime, always set up key events before celebrating, always wait 48 hours and audit again.",
      },
      { type: "h2", text: "What to do after the setup is right" },
      {
        type: "p",
        text:
          "Once the data is trustworthy, the next problem is making sense of it weekly. That's exactly what Plainly solves — connect the GA4 you just configured, and we'll send you a plain-English read-out every Monday morning. The free demo on the homepage shows what the report looks like.",
      },
    ],
  },
  {
    slug: "best-google-analytics-reporting-tools-2026",
    title: "The best Google Analytics reporting tools in 2026 (and when each one is overkill)",
    description:
      "An honest, side-by-side look at 7 ways to turn GA4 into something readable — from free built-in tools to enterprise dashboards. With straight talk on price, learning curve, and who each one is actually for.",
    date: "2026-03-01",
    readMinutes: 12,
    keywords: [
      "GA4 reporting tools",
      "Google Analytics alternatives",
      "best GA4 dashboard",
      "Looker Studio alternative",
      "Whatagraph alternative",
      "Google Analytics for small business",
      "GA4 reporting software comparison",
    ],
    author: { name: "Nicholas Steer", role: "Founder, Plainly" },
    content: [
      {
        type: "p",
        text:
          "Google Analytics 4 is a powerful data warehouse pretending to be a reporting tool. It collects everything, exposes most of it, and explains almost none of it. That's why a whole category of products exists: tools that sit on top of GA4 and turn raw data into something a human can actually read.",
      },
      {
        type: "p",
        text:
          "But these tools are not interchangeable. Some are free and limited. Some are $300/month and built for agencies running 40 client reports a week. Some are AI-powered, some are dashboard-heavy, some are pure data pipelines that don't do reporting at all. Pick the wrong one and you'll either drown in features you don't need or hit a wall on the one feature you actually do.",
      },
      {
        type: "p",
        text:
          "This is an honest, opinionated guide to the 7 ways most people deal with GA4 in 2026. We'll cover what each one does well, where each one falls down, and — most importantly — who each one is for and when it's overkill. Yes, Plainly is on the list; we'll be straight about where we fit and where we don't.",
      },
      { type: "h2", text: "First: do you actually need a GA4 reporting tool?" },
      {
        type: "p",
        text:
          "Before spending a dollar, answer one question honestly: are you trying to understand your traffic, or are you trying to present your traffic to someone else? Those are very different problems with very different solutions.",
      },
      {
        type: "ul",
        items: [
          {
            label: "If you're trying to understand it",
            body:
              "You want a tool that summarizes, explains, and prioritizes — something that tells you 'these three things changed this week and here's why.' You want a narrator, not a dashboard.",
          },
          {
            label: "If you're trying to present it",
            body:
              "You want a tool that produces beautiful, custom-branded reports — dashboards, exports, scheduled PDFs. You want a designer, not a narrator.",
          },
          {
            label: "If you're trying to do both",
            body:
              "You probably need two tools, or one tool that does both moderately well. Most products in this space do one or the other, not both.",
          },
        ],
      },
      {
        type: "p",
        text:
          "With that lens in mind, here's how the 7 most common options shake out.",
      },
      { type: "h2", text: "1. Google Analytics Intelligence (built into GA4)" },
      {
        type: "p",
        text:
          "Cost: free. Setup: zero. Where to find it: the little gem-shaped 'Insights' icon in the top right of any GA4 report.",
      },
      {
        type: "p",
        text:
          "Most GA4 users don't realize Google built a basic AI assistant directly into the product. Click 'Insights' and you can ask plain-English questions like 'how did organic traffic change last week' or 'what's my best landing page.' Behind the scenes, GA4 runs the query and returns a chart with a one-line natural-language summary.",
      },
      {
        type: "p",
        text:
          "Where it wins: it's free, it's already in your account, and for simple lookups (\"what was my top source last month?\") it's faster than building a custom report.",
      },
      {
        type: "p",
        text:
          "Where it falls down: the summaries are one sentence and they're often shallow — \"Sessions increased by 12.4% compared to the previous period\" is a sentence, not an explanation. There's no week-over-week emailed report. There's no concept of 'and here's what to try next.' It tells you the number; it doesn't tell you the story.",
      },
      {
        type: "p",
        text:
          "Who it's for: anyone who has never explored the Insights panel and just wants to start somewhere. Open it, click around for 20 minutes, see if it gets you what you need. If yes, great — save your money. If no, you've now scoped your real requirement, which makes the rest of this list useful.",
      },
      { type: "h2", text: "2. Google Looker Studio (formerly Data Studio)" },
      {
        type: "p",
        text:
          "Cost: free for most uses. Setup: a few hours to build your first useful dashboard; an afternoon if you start from a template. Where to find it: lookerstudio.google.com.",
      },
      {
        type: "p",
        text:
          "Looker Studio is Google's free dashboarding tool. It connects directly to GA4 (plus Search Console, Ads, Sheets, and dozens of other sources) and lets you build custom dashboards with charts, tables, scorecards, and filter controls.",
      },
      {
        type: "p",
        text:
          "Where it wins: it's free. It's powerful. It produces beautiful, sharable dashboards that you can embed in client deliverables. It handles cross-source blending (GA4 + Search Console + Google Ads in one report) better than almost anything else at its price point. The community template gallery means you can clone a polished agency-style template in 30 seconds.",
      },
      {
        type: "p",
        text:
          "Where it falls down: it's still a dashboarding tool, which means YOU have to know what to put on the dashboard. If you couldn't read GA4 to begin with, Looker Studio doesn't fix that — it just gives you a prettier version of the same confusion. Real-time updates can be slow. There's no AI narrative layer, no week-over-week email, no 'why did this happen.'",
      },
      {
        type: "p",
        text:
          "Who it's for: people who already roughly understand their data and want to package it visually. If you're an agency producing monthly client reports and you have someone on your team who knows GA, Looker Studio is probably the best free choice on the planet. If you're a solo founder who just wants to know what changed this week, it's overkill.",
      },
      { type: "h2", text: "3. Plainly" },
      {
        type: "p",
        text:
          "Cost: $7 one-time analysis, $18/month for unlimited + weekly emails, $39/month for agency white-label. Setup: 60 seconds — sign in with Google, connect GA4 with one click, pick a date range.",
      },
      {
        type: "p",
        text:
          "Plainly is the tool we built and the reason this list exists. Plainly reads your GA4 data, compares it week-over-week (or any custom range), and writes a plain-English summary using Anthropic's Claude Sonnet 4.5. You get a 4-5 paragraph narrative, a list of metric explanations, observed patterns, wins, concerns, and prioritized suggestions for what to try next. On the $18/month Business plan, the whole thing arrives in your inbox automatically every Monday morning.",
      },
      {
        type: "p",
        text:
          "Where it wins: it's the only tool on this list whose primary output is text, not charts. If your goal is 'understand,' not 'present,' Plainly will get you there faster than anything else. Setup takes a minute. There's a free demo dataset so you can see exactly what you get before paying. The Monday-morning email means you actually engage with your data instead of letting it sit unloved in a tab.",
      },
      {
        type: "p",
        text:
          "Where it falls down: we are not a custom dashboard builder. If you need to design a 14-page branded client report with custom charts, Plainly is the wrong tool. The Agency plan does include PDF export and white-labeling, but it's a single, well-designed report — not a fully customizable canvas. We also don't blend across multiple data sources (yet); it's GA4 (or CSV) only.",
      },
      {
        type: "p",
        text:
          "Who it's for: small business owners, freelancers, marketing managers, and small agencies whose primary question is 'what changed this week and what should I do about it?' If that's you, Plainly is almost certainly the best ROI on this list. If you're an enterprise agency producing 50 highly customized client deliverables a month, you need something else.",
      },
      { type: "h2", text: "4. Whatagraph" },
      {
        type: "p",
        text:
          "Cost: starts at $199/month at the time of writing; agency plans go higher. Setup: a few hours to connect sources and build your first cross-channel dashboard.",
      },
      {
        type: "p",
        text:
          "Whatagraph is built squarely for agencies. It connects to 45+ data sources (GA4, Meta Ads, Google Ads, TikTok, LinkedIn, Mailchimp, etc.), blends them into unified dashboards, and lets you schedule automated white-labeled client reports.",
      },
      {
        type: "p",
        text:
          "Where it wins: cross-channel reporting is its superpower. If you run paid ads on 4 platforms and need to show ROAS across all of them next to your GA4 organic numbers, Whatagraph is one of the cleanest ways to do it. The drag-and-drop builder is genuinely good. White-label client portals work.",
      },
      {
        type: "p",
        text:
          "Where it falls down: it's expensive — at $199/month, it's a hard sell for any business that isn't running a multi-channel agency. The learning curve is non-trivial. And like all dashboard tools, it tells you what your numbers are, not what they mean. You'll still need someone on your team to read those dashboards and translate.",
      },
      {
        type: "p",
        text:
          "Who it's for: established agencies managing 5+ clients with multi-channel paid media. If that's your business, Whatagraph or one of its near-clones (DashThis, AgencyAnalytics) is probably worth the cost. Solo founders, single-channel businesses, or anyone whose client count is under five: definitely overkill.",
      },
      { type: "h2", text: "5. Databox" },
      {
        type: "p",
        text:
          "Cost: free tier, paid plans start around $59/month. Setup: 30-60 minutes for first dashboard.",
      },
      {
        type: "p",
        text:
          "Databox sits in an interesting middle position: it's a dashboard tool with strong mobile apps and a heavy emphasis on goal-tracking. Connect GA4, set a goal (e.g. 'increase organic sessions to 10,000/month'), and Databox will track progress, send mobile push notifications when you hit milestones, and surface alerts when metrics drop.",
      },
      {
        type: "p",
        text:
          "Where it wins: the mobile experience is the best in this category by a wide margin. The goal-tracking layer adds a useful psychological hook — you're not just staring at a chart, you're tracking progress toward something. Alerts work well.",
      },
      {
        type: "p",
        text:
          "Where it falls down: like Looker Studio and Whatagraph, it's a dashboard tool at heart. If you can't read GA4, Databox doesn't read it for you — it just shows you the same numbers on a phone. The free tier is quite limited and you hit the paid tier fast once you connect more than a couple sources.",
      },
      {
        type: "p",
        text:
          "Who it's for: founders and marketing managers who travel a lot and want to check on their key metrics from their phone between meetings. The mobile app alone is worth it if that describes you. For desktop-first analysts, it doesn't add much over Looker Studio.",
      },
      { type: "h2", text: "6. Supermetrics" },
      {
        type: "p",
        text:
          "Cost: starts around $39/month per data source. Setup: 15 minutes to install and pull your first data.",
      },
      {
        type: "p",
        text:
          "Supermetrics doesn't really belong on a 'reporting tools' list, but it's so commonly considered alongside them that it's worth a mention. Supermetrics is a data pipeline: it pulls data out of GA4 (and 100+ other sources) and pushes it into Google Sheets, Excel, BigQuery, or your data warehouse of choice. It's the plumbing under the floor of a custom reporting setup.",
      },
      {
        type: "p",
        text:
          "Where it wins: if you have an analyst on your team who builds custom reports in Sheets or Excel, Supermetrics removes the entire 'export, paste, refresh' tedium. It's the de facto standard for that use case.",
      },
      {
        type: "p",
        text:
          "Where it falls down: it produces zero reports on its own. If you don't have the analyst, Supermetrics is just an expensive way to move data from one place where you don't understand it to another place where you also don't understand it.",
      },
      {
        type: "p",
        text:
          "Who it's for: agencies and in-house teams with a dedicated analyst who already has a custom reporting workflow in Sheets. Anyone else: not for you.",
      },
      { type: "h2", text: "7. Custom GPT or ChatGPT + manual exports" },
      {
        type: "p",
        text:
          "Cost: $20/month (ChatGPT Plus). Setup: 5 minutes for the manual workflow.",
      },
      {
        type: "p",
        text:
          "An increasingly common DIY approach: export your GA4 data to CSV, drop it into ChatGPT (or build a custom GPT pointed at your data), ask it to summarize. For one-off ad-hoc analysis, this actually works surprisingly well.",
      },
      {
        type: "p",
        text:
          "Where it wins: it's flexible. You can ask any question you can think of and get an answer in plain English. It's cheap. You're not locked into a vendor's report format.",
      },
      {
        type: "p",
        text:
          "Where it falls down: it's manual every single time. You have to remember to do it. There's no week-over-week automated email. There's no consistent format. You have to remember what to ask. And privacy: every CSV you paste into ChatGPT may be retained for training depending on your settings — read the data usage policy before you paste production data.",
      },
      {
        type: "p",
        text:
          "Who it's for: hobbyists, infrequent users, and anyone who wants total control and is willing to do the work every single time. If you'd rather not remember to do it weekly, you'll want a tool that automates the pull and the summary — which is essentially what Plainly is.",
      },
      { type: "h2", text: "Side-by-side: cost vs. who it's for" },
      {
        type: "p",
        text:
          "Quick gut-check version:",
      },
      {
        type: "ul",
        items: [
          { label: "GA4 Intelligence", body: "Free. Built in. Try this first; it might be enough." },
          { label: "Looker Studio", body: "Free. Best for people who already know GA4 and want pretty dashboards." },
          { label: "Plainly", body: "$7–$39. Best for people who want plain-English explanations, not dashboards." },
          { label: "Whatagraph", body: "$199+. Best for multi-channel agencies." },
          { label: "Databox", body: "$59+. Best for mobile-first operators tracking specific goals." },
          { label: "Supermetrics", body: "$39+. Best for teams with a dedicated analyst building custom reports." },
          { label: "ChatGPT + manual", body: "$20. Best for one-off analysis if you're disciplined." },
        ],
      },
      { type: "h2", text: "How to actually pick" },
      {
        type: "p",
        text:
          "Don't start with the tool — start with the decision you need to make. Three questions, in order:",
      },
      {
        type: "ul",
        items: [
          {
            label: "How often do you actually want to look at your data?",
            body:
              "If your honest answer is 'once a week or less,' you need automation. A dashboard that requires you to log in and click around will gather dust. A weekly email will not. Be honest with yourself here — the gap between 'I should check my analytics every week' and 'I actually do' is where most reporting investments die.",
          },
          {
            label: "Are you trying to understand, or trying to present?",
            body:
              "Understand → narrative tool (Plainly, GA4 Intelligence, custom ChatGPT). Present → dashboard tool (Looker Studio, Whatagraph, Databox). If you try to use a dashboard tool to understand, you'll just be looking at prettier confusion. If you try to use a narrative tool to present, your client will want more visuals than you can give them.",
          },
          {
            label: "How many data sources do you need to blend?",
            body:
              "Just GA4 → almost any of these work. GA4 + Search Console → Looker Studio is the sweet spot. 4+ sources including paid ads, social, and email → Whatagraph or DashThis territory. Be careful not to over-buy here. Blending sounds powerful in theory but each connector adds maintenance, complexity, and (in many tools) a per-source cost line. Start with one source done well.",
          },
        ],
      },
      {
        type: "p",
        text:
          "If you answered 'weekly or less, understand, just GA4' — which describes the vast majority of small businesses, freelancers, and one-person marketing teams — you'll save yourself a year of pain by skipping straight to a narrative tool. That's Plainly's exact niche, which is why we built it for you specifically.",
      },
      { type: "h2", text: "A few questions we get asked all the time" },
      {
        type: "p",
        text:
          "Three questions we get over and over when people are picking between these tools, and our honest answers:",
      },
      {
        type: "ul",
        items: [
          {
            label: "\"Can I just use ChatGPT — why pay for Plainly?\"",
            body:
              "You can, and for one-off analyses it works fine. The reason people end up paying for Plainly is consistency: you stop having to remember to do it, the format stays the same every week so trends are easier to spot, and the data never leaves a privacy-compliant pipeline (your GA data isn't sent to OpenAI; we use Anthropic Claude with a no-training contract). If you'll genuinely sit down every Monday and do it manually, you don't need us.",
          },
          {
            label: "\"Is Looker Studio really enough for a small business?\"",
            body:
              "For some, yes — especially if you have someone on your team who already knows how to read GA4 and just wants the data in a cleaner format. For most small business owners who don't speak analytics natively, Looker Studio just gives them a prettier version of the same confusion. Both tools can coexist: use Plainly for the weekly read-out, use Looker Studio for the once-a-quarter client report.",
          },
          {
            label: "\"Won't all these tools be obsolete when ChatGPT just reads my GA4 directly?\"",
            body:
              "Probably yes, eventually. But there's a real product layer between 'AI can read my data' and 'AI tells me what I actually need to know.' Picking which metrics matter, comparing the right time periods, ranking what to act on, formatting it as a weekly email — all of that takes deliberate design. The companies that survive the AI commoditization wave will be the ones with strong opinions about the output, not just access to the input. That's the bet Plainly is making.",
          },
        ],
      },
      {
        type: "p",
        text:
          "The honest version: every tool on this list will be different in two years. Pick the one that solves the problem you have today, not the one with the biggest feature list. You can always switch.",
      },
      {
        type: "quote",
        text:
          "The best reporting tool is the one you'll actually open. Pick the one that fits your habits, not the one with the most features on the comparison page.",
      },
      { type: "h2", text: "Try Plainly free — see if it fits" },
      {
        type: "p",
        text:
          "If after all that you think 'understand, weekly, GA4 only' is you — try Plainly's free demo from the homepage. No credit card. You'll see exactly what a Plainly analysis looks like on a real-looking demo dataset, and you can decide in 90 seconds whether the narrative format is what you've been missing. If it's not, no harm done; you've narrowed your search down faster than another month of opening GA4 and closing it would have.",
      },
    ],
  },
  {
    slug: "why-ga4-traffic-dropped-this-week",
    title: "Why your GA4 traffic dropped this week (5 common culprits, ranked)",
    description:
      "Your Google Analytics is showing a scary cliff. Before you panic, check these five things in order — they explain about 95% of unexpected GA4 traffic drops.",
    date: "2026-02-22",
    readMinutes: 6,
    keywords: [
      "GA4 traffic drop",
      "Google Analytics traffic dropped",
      "why did my website traffic drop",
      "GA4 sessions decreased",
      "traffic dropped suddenly",
      "website traffic decline",
    ],
    author: { name: "Nicholas Steer", role: "Founder, Plainly" },
    content: [
      {
        type: "p",
        text:
          "It's Monday morning, you open GA4, and your sessions chart looks like the side of a cliff. Before you spend three hours googling \"Google algorithm update,\" run through these five culprits in order. About 95% of unexpected GA4 traffic drops fall into one of them — and the first one is the most common AND the most overlooked.",
      },
      { type: "h2", text: "1. Your tracking actually broke" },
      {
        type: "p",
        text:
          "Yes, this is #1, and yes, almost nobody checks it first. If your GA4 tag stopped firing — because someone edited the site, the GTM container got republished, or a new privacy plugin started blocking it — the drop will look exactly like a real traffic drop. Except it isn't. It's just that you stopped counting.",
      },
      {
        type: "p",
        text:
          "How to check in 30 seconds: open your live site in an incognito tab → DevTools → Network tab → filter for \"collect\" → reload the page. If you don't see a request to google-analytics.com/g/collect, your tag is broken. Fix that first; everything else is irrelevant until you can trust the numbers.",
      },
      { type: "h2", text: "2. One specific channel collapsed (not the total)" },
      {
        type: "p",
        text:
          "If overall sessions dropped 30%, the question isn't \"what happened to my traffic\" — it's \"which channel went silent.\" Open Reports → Acquisition → Traffic acquisition and compare last week to the prior week. You're looking for a single row that fell off a cliff while others stayed roughly flat. The pattern tells you the cause:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Organic Search collapsed",
            body:
              "Either a Google algorithm update hit you (check Search Console for ranking drops on key queries), or a key page got deindexed (check Search Console → Pages → Indexed for sudden 'Not indexed' entries).",
          },
          {
            label: "Direct collapsed",
            body:
              "Usually means GA4 stopped attributing properly — check Reports → Tech → User attribute coverage for sudden gaps. Or a major email/SMS campaign that brought direct traffic ended.",
          },
          {
            label: "Social or Referral collapsed",
            body:
              "A specific post stopped being shared, or a referring site removed the link. Open the channel detail and look at the top source/medium pair to find the missing one.",
          },
          {
            label: "Paid Search collapsed",
            body:
              "Almost always means your Google Ads campaign paused, ran out of budget, or had its bids tanked. Check Google Ads first, GA4 second.",
          },
        ],
      },
      { type: "h2", text: "3. Google rolled out an algorithm update" },
      {
        type: "p",
        text:
          "Google ships major updates (Core, Helpful Content, Spam, Reviews) several times a year, plus smaller daily tweaks. When a big one rolls out, sites can lose 20-80% of their organic search traffic overnight. The fastest way to check: visit Search Engine Land's algorithm update tracker (searchengineland.com/library/google/google-algorithm-updates) and see if there was a confirmed update in the last 14 days.",
      },
      {
        type: "p",
        text:
          "If yes: check Google Search Console → Performance → compare the dates against the update. If your impressions tanked too (not just clicks), it's almost certainly the update. The fix is rarely fast — Google explicitly says \"create helpful, people-first content\" — but at least you know what you're dealing with.",
      },
      { type: "h2", text: "4. Seasonal, calendar, or news event" },
      {
        type: "p",
        text:
          "Some drops are completely normal and you just forgot they happen. Examples that catch people every year:",
      },
      {
        type: "ul",
        items: [
          {
            label: "US holidays",
            body:
              "Thanksgiving week, the week between Christmas and New Year, and the week of July 4th typically see 30-50% B2B traffic drops. B2C sees the opposite spike.",
          },
          {
            label: "Day-of-week shifts",
            body:
              "If you're comparing Mon-Sun to Tue-Mon, you're comparing 2 weekends to 1 weekend. Always compare same days of the week.",
          },
          {
            label: "Local news",
            body:
              "If a major news event dominated your audience's region (election, weather, sports final), your traffic dipped because people were watching that instead.",
          },
          {
            label: "Email cadence shifts",
            body:
              "If you usually send a Tuesday newsletter and you skipped it this week, that's your drop.",
          },
        ],
      },
      { type: "h2", text: "5. Site speed or a technical issue" },
      {
        type: "p",
        text:
          "If your site loaded slowly all week — or worse, returned 500 errors for some users — visitors bailed before they registered as sessions. Check your hosting status page, look at PageSpeed Insights for your most-trafficked pages, and check Search Console → Page Experience → Core Web Vitals. A sudden LCP regression can quietly drop conversions 10-30%.",
      },
      {
        type: "quote",
        text:
          "Half the time, the GA4 chart isn't lying — it's telling you something you forgot. The other half, it IS lying, because your tracking quietly broke.",
      },
      { type: "h2", text: "The 60-second triage checklist" },
      {
        type: "p",
        text:
          "When you see a drop, run through this in order and stop at the first one that explains it:",
      },
      {
        type: "ul",
        items: [
          { label: "Is the tag still firing", body: "DevTools → Network → \"collect\" request present?" },
          { label: "Did one channel drop", body: "Acquisition report, compare period-over-period, look for the outlier row." },
          { label: "Did Google ship an update", body: "Check Search Engine Land in the last 14 days." },
          { label: "Is it a holiday or calendar artifact", body: "Look at the same week last year." },
          { label: "Are pages loading fast and returning 200s", body: "Search Console + uptime monitor." },
        ],
      },
      { type: "h2", text: "How to actually fix a tracking break" },
      {
        type: "p",
        text:
          "If you confirmed the issue is #1 (your tag stopped firing), the fix path depends on where you installed GA4. About 70% of the time the culprit is one of three things: a developer republished the site and left out the GA4 snippet, your Google Tag Manager container was rolled back to an older version, or a recently-installed cookie-consent plugin is blocking analytics by default. Walk through each in that order. Start by opening your GA4 admin → Data Streams → click the active stream → 'Tagging Instructions' → 'View tag instructions' to confirm the exact gtag.js snippet that should be on every page. Open your live site in a private window, view source, and search for your measurement ID (it starts with G-). If it's missing, your developer or CMS is the problem. If it's present but not firing, your consent manager almost certainly is.",
      },
      {
        type: "p",
        text:
          "Cookie-consent and privacy plugins like CookieYes, Iubenda, Termly, OneTrust, and Cookiebot ship with 'block analytics until consent' on by default. If you recently installed one — or it auto-updated — your GA4 may have gone silent on 70-90% of EU visitors and an unpredictable slice of US ones depending on the plugin's geo rules. The fix is not to disable the consent banner (you usually need it for compliance), but to configure 'Google Consent Mode v2' inside the plugin so that anonymous, cookieless pings still flow to GA4 even when a visitor declines cookies. Most modern consent plugins have a one-click Consent Mode v2 toggle now; turn it on and the chart will start refilling within 24-48 hours.",
      },
      { type: "h2", text: "How to confirm a channel collapse vs. a real drop" },
      {
        type: "p",
        text:
          "When #2 looks like the answer, validate it before you act. Open Reports → Acquisition → Traffic acquisition and set the comparison to the previous week of the same length. Hover over the chart and you'll see two lines stacked — current and prior. If a single channel (say, Organic Search) lost 40% while every other channel held flat, that's a true channel collapse and you treat it like a targeted investigation. If every channel lost 10-15% in roughly the same proportion, that's an across-the-board drop and it almost always points to a non-marketing cause: a holiday, a site outage, a tag break, or seasonality. Treating an across-the-board drop like a channel collapse will send you down the wrong rabbit hole for hours.",
      },
      {
        type: "p",
        text:
          "Inside a true Organic Search collapse, the single most useful next click is Search Console → Performance → Compare last 7 days vs. previous 7 days, then segment by Page and by Query. You're looking for whether impressions also dropped (a ranking or indexing problem) or whether impressions stayed flat but clicks fell (a click-through-rate problem, usually caused by a new featured snippet, a new AI Overview, or a competitor outranking you on a juicy SERP feature). The distinction matters because the fix is completely different: a ranking drop needs content or backlink work, while a CTR drop needs title-tag and meta-description rewrites you can ship the same afternoon.",
      },
      { type: "h2", text: "What Google algorithm updates usually do" },
      {
        type: "p",
        text:
          "If you've confirmed Google rolled out an update, calibrate your expectations before you start changing things. Core updates run for 1-3 weeks; the chart you see on day 5 is rarely the chart you'll see on day 20. Many sites see a partial bounce-back during the rollout window as Google's models settle. Resist the urge to slash content, delete pages, or do a sitewide rewrite during week one — you'll be solving for a snapshot that hasn't finished developing. Instead, document exactly which pages lost rankings and which queries shifted, then come back after the rollout is confirmed complete (Google announces this in their search status dashboard) and compare the before/after side-by-side.",
      },
      {
        type: "p",
        text:
          "If you're hit by a Helpful Content or Reviews update specifically, the fix pattern is well-documented: thin affiliate content, AI-generated filler, and pages that exist only to rank (not to answer a real question) take the brunt of the damage. The recovery path is to consolidate or remove your weakest 10-20% of pages and rewrite the rest from a real point of view with first-hand experience. Google has published an extensive 'self-assessment' questionnaire for the Helpful Content update — search for 'Google helpful content self-assessment' and walk through it page by page on your worst-hit URLs. Realistic recovery timelines are 3-6 months, sometimes longer, so don't expect a quick win.",
      },
      { type: "h2", text: "Sanity-checking 'it's just seasonality'" },
      {
        type: "p",
        text:
          "The fastest way to confirm or rule out seasonality is to open the same week one year ago and compare. In GA4's date picker, set the current range to last week, then click Compare → Custom → and pick the same days from 52 weeks ago (e.g., Mon-Sun of last week vs. Mon-Sun of the same calendar week in the prior year). If your traffic looks roughly proportional — both are down 30% vs. their respective baselines, or both spiked the week before — you're looking at a calendar effect, not a problem. The same applies for holidays that move (Easter, Thanksgiving): year-over-year same-week comparison normalizes the calendar shift for you.",
      },
      {
        type: "p",
        text:
          "One trap to watch: if your business has grown 50% year-over-year, an absolute drop this year can look catastrophic when it's really proportional to last year's smaller baseline. Always look at percentage change vs. the same period one year ago, not just the raw number. If last year you dropped 28% during Thanksgiving week and this year you dropped 30%, you're not having a crisis — you're having a Thursday in November.",
      },
      { type: "h2", text: "When a site-speed issue is hiding the real problem" },
      {
        type: "p",
        text:
          "Site-speed-driven drops are sneaky because they don't always show up as a clean cliff. More often, they show up as a slow, week-over-week erosion of conversion rate that accumulates into a noticeable session drop because returning visitors stop coming back. To check, open Google Search Console → Page Experience → Core Web Vitals and look at the 'Poor' and 'Needs improvement' counts. If those numbers grew last week, you have a regression. Then open PageSpeed Insights for your top three landing pages and check the Largest Contentful Paint (LCP), Interaction to Next Paint (INP), and Cumulative Layout Shift (CLS) values. Any of those moving from green to amber or red on mobile is a real-world traffic problem, not a vanity metric.",
      },
      {
        type: "p",
        text:
          "The most common recent culprits we see are: a developer added a heavy hero video or carousel that pushed LCP past 2.5 seconds, a new live-chat widget or pixel was added without lazy-loading, or a third-party font swap is causing 400-800ms of blocking time. Removing or deferring those scripts usually restores traffic within 2-3 weeks as Google re-crawls and re-scores the pages. If you've ruled out everything else and your traffic is still down, this is the place 90% of teams forget to check.",
      },
      { type: "h2", text: "A worked example: the 'Tuesday cliff' diagnosis" },
      {
        type: "p",
        text:
          "Here's how a real diagnosis runs end-to-end. A boutique e-commerce store opens GA4 on a Tuesday and sees sessions down 38% week-over-week. Panic sets in. Step 1: incognito tab, DevTools, /g/collect request is firing — tracking is intact. Step 2: Traffic acquisition shows Organic Search down 5%, Direct down 6%, but Paid Search down 91% — there's the culprit. Step 3: Google Ads tab shows the campaign was paused on Saturday because the monthly budget cap hit. Total time: 4 minutes. Action: reset the cap, schedule a recurring weekly check, and add a budget-pacing alert in Google Ads. No content rewrite, no algorithm panic, no consultant call.",
      },
      {
        type: "p",
        text:
          "The exact same dataset, interpreted by someone who skipped step 2, often turns into a multi-day investigation that ends with the team blaming SEO, blaming a designer for a recent layout change, or hiring an agency to 'audit' the site. The investigation order matters more than your tooling. Always confirm tracking is alive, then confirm whether the drop is one channel or all channels, then work the cause that matches the pattern. That sequence alone will save you more time than any new dashboard you buy.",
      },
      { type: "h2", text: "Or, just let Plainly do this for you" },
      {
        type: "p",
        text:
          "This whole triage process is exactly what Plainly automates every Monday morning. We compare week-over-week, identify which specific channel changed, surface the likely cause in plain English, and tell you the one thing to check next. You read a paragraph instead of clicking through six reports. Try the free demo from the homepage — no card required.",
      },
    ],
  },
  {
    slug: "how-to-read-ga4-reports-plain-english",
    title: "How to actually read your GA4 report (a 5-minute, plain-English guide)",
    description:
      "GA4 is overwhelming on purpose. Here's the 5-minute version: what to look at, what to ignore, and how to spot the one number that actually matters for your business this week.",
    date: "2026-02-15",
    readMinutes: 5,
    keywords: [
      "GA4",
      "Google Analytics 4",
      "how to read GA4",
      "Google Analytics for small business",
      "plain English analytics",
    ],
    author: { name: "Nicholas Steer", role: "Founder, Plainly" },
    content: [
      {
        type: "p",
        text:
          "If you've opened Google Analytics 4 lately, blinked at 40 widgets, and immediately closed the tab — you're not alone. GA4 was rebuilt for advertisers and product teams, not the average small-business owner who just wants to know whether the website is working. Here's the 5-minute version of what to actually look at.",
      },
      { type: "h2", text: "1. Start with one question, not the dashboard" },
      {
        type: "p",
        text:
          "Before you even open GA4, write down one specific question. Something like: \"Did my new landing page bring in more leads than the old one?\" or \"Is my Pinterest traffic still converting?\" If you walk in without a question, you'll walk out with 40 numbers and zero decisions.",
      },
      { type: "h2", text: "2. The four reports that actually matter" },
      {
        type: "p",
        text:
          "GA4 has dozens of reports, but for 90% of small-business questions you only need four:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Reports → Acquisition → Traffic acquisition",
            body:
              "This tells you where your visitors came from (Google search, Instagram, direct typing of the URL, etc.). It's the answer to \"is my SEO working?\" or \"is my Pinterest paying off?\"",
          },
          {
            label: "Reports → Engagement → Pages and screens",
            body:
              "Which pages people actually read. Sort by views, then look at the average engagement time. A page with lots of views and 8 seconds of engagement is broken; a page with fewer views and 2 minutes of engagement is gold.",
          },
          {
            label: "Reports → Engagement → Landing page",
            body:
              "Where people land first. This is the report that tells you which page is your real \"front door.\" Usually it's not your homepage.",
          },
          {
            label: "Reports → Monetization → Ecommerce purchases (if applicable)",
            body:
              "Conversions tied to actual revenue. If you don't sell online, configure a \"key event\" for your contact form or booking link and watch that instead.",
          },
        ],
      },
      { type: "h2", text: "3. The three numbers that almost always lie" },
      {
        type: "p",
        text:
          "Some GA4 metrics are technically accurate but practically useless for a small business. Don't lose sleep over these in isolation:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Bounce rate",
            body:
              "GA4 redefined this from Universal Analytics. A \"bounce\" in GA4 is anyone who spent less than 10 seconds AND didn't trigger a key event AND didn't view a second page. A high bounce rate on a blog post is actually fine — they read it and left.",
          },
          {
            label: "Users vs. Active Users vs. Total Users",
            body:
              "GA4 has multiple \"user\" counts. Pick one (we recommend Active Users) and stick with it. Comparing different user metrics across reports will drive you mad.",
          },
          {
            label: "Real-time",
            body:
              "Fun to watch, useless for decisions. Real-time only shows the last 30 minutes, and the sample is too small to draw any conclusion. Close that tab.",
          },
        ],
      },
      { type: "h2", text: "4. The week-over-week comparison is your friend" },
      {
        type: "p",
        text:
          "Single numbers are meaningless. \"500 sessions\" is good if you usually get 300, terrible if you usually get 2,000. Always compare against the previous week (or month, or year) to put any metric in context. In GA4, every report has a date picker in the top right — click it, pick your range, then click \"Compare\" and pick the same length right before. That single trick will tell you more in 30 seconds than 30 minutes of dashboard staring.",
      },
      { type: "h2", text: "5. When something changes, find the WHY" },
      {
        type: "p",
        text:
          "If sessions dropped 20% last week, don't just panic — break it down. In Traffic acquisition, ask: did one specific channel drop (e.g., \"Organic Search\" fell off a cliff = Google algorithm update or a deindexed page)? Or did all channels drop equally (likely a holiday, weather event, or site outage)? The pattern of the drop tells you the cause.",
      },
      {
        type: "quote",
        text:
          "The numbers themselves are the easy part. The hard part is figuring out which number actually matters this week.",
      },
      { type: "h2", text: "6. The one-question rule, applied" },
      {
        type: "p",
        text:
          "Most GA4 frustration comes from treating the dashboard like a feed instead of a tool. A feed (Instagram, TikTok, email) invites passive scrolling — open it, browse, close it. GA4 looks like that too, with its widgets and sparklines and percent-change badges, but it's the wrong mental model. GA4 is closer to a tax form than a feed: useful only when you arrive with a specific question and a specific time window. The fastest way to retrain the habit is to write your question on a sticky note before opening the tab. Examples: 'Did the Pinterest pin from Feb 3 send any traffic that converted?', 'Is the new homepage hero better than the old one for newsletter signups?', 'Why did the blog get 3x its normal traffic last Tuesday?' Each of these takes 5-10 minutes inside GA4 if you know where to look, and 90 minutes if you don't.",
      },
      {
        type: "p",
        text:
          "Once you have your question, every click should be in service of answering it. If you find yourself two reports deep and you can't articulate how the current screen relates to your sticky-note question, you've drifted. Close the tab, re-read the question, and start again. This sounds obvious, but it's the single biggest behavior change that separates people who get value out of GA4 from people who don't.",
      },
      { type: "h2", text: "7. The five clicks that answer 80% of questions" },
      {
        type: "p",
        text:
          "There's a small, repeatable click path that solves most small-business analytics questions. Memorize it and you'll reach answers 5-10x faster than people who 'explore.' Here's the canonical sequence:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Click 1 — pick the time window",
            body:
              "Top-right date picker. Set the range that matches your question (last 7 days, last 30 days, last quarter). Then enable Compare and pick the immediately preceding period of the same length. Comparison is non-optional — single numbers are meaningless.",
          },
          {
            label: "Click 2 — go to Acquisition → Traffic acquisition",
            body:
              "This answers 'where did the visitors come from.' Sort by Sessions descending. Note which channels grew, shrank, or stayed flat. If one channel moved dramatically, that's almost always the story.",
          },
          {
            label: "Click 3 — drill into the moved channel",
            body:
              "Click the channel name to filter the rest of the report. Now switch the dimension to Session source / medium to see the specific source (e.g., google / organic, instagram / referral, newsletter-feb / email).",
          },
          {
            label: "Click 4 — switch to Engagement → Landing page",
            body:
              "Filter is still applied. This tells you which page the visitors from that source landed on. The combination of 'where they came from' + 'where they landed' is the entire customer journey for a 90% accurate first-pass story.",
          },
          {
            label: "Click 5 — check the key event",
            body:
              "Scroll right (or add the column) for your key event count. Did conversions move with sessions, or did sessions move but conversions stay flat? Those two patterns mean very different things and should drive very different next steps.",
          },
        ],
      },
      { type: "h2", text: "8. Engagement time is the metric you've been ignoring" },
      {
        type: "p",
        text:
          "GA4 buried it, but average engagement time per session is the most underrated metric in the platform. It's the closest thing GA4 has to 'are people actually reading this' and it ages much better than bounce rate. A good rule of thumb: for blog and informational content, an average engagement time below 30 seconds means most readers left before the article loaded its main argument. Above 90 seconds, you have a piece of content that's working. Above 3 minutes, you have something that's almost certainly converting somewhere downstream even if attribution doesn't show it.",
      },
      {
        type: "p",
        text:
          "To find it, open Reports → Engagement → Pages and screens, then look at the 'Average engagement time' column. Sort descending and you'll immediately see which pages are real engines versus which ones are getting traffic but failing to hold attention. This is also the single best lens for identifying which old blog posts deserve to be refreshed — high traffic + low engagement is a clear refresh candidate, while low traffic + high engagement is a candidate for promotion (newsletter feature, internal links from your hottest pages, an SEO tune-up).",
      },
      { type: "h2", text: "9. How to set up the only custom report you'll actually use" },
      {
        type: "p",
        text:
          "GA4's Explore section is powerful, intimidating, and 95% of people never use it. The 5% who do tend to build one custom report and reach for it every week. Here's the one to build: open Explore → Free form, set Dimensions to Session source/medium and Landing page, set Metrics to Sessions, Engaged sessions, Average engagement time, and your key event count, then save it as 'Weekly traffic story.' Now every Monday you open one report instead of five, and you see source + landing page + engagement + conversion in a single table.",
      },
      {
        type: "p",
        text:
          "Two pro tips on top of that template. First, add a comparison segment for 'previous period' so the weekly numbers always show up as deltas, not absolute values. Second, add a filter to exclude any landing page containing '?utm_' so you don't double-count campaign URLs that GA4 sometimes treats as separate pages. Those two tweaks make the report dramatically more readable, and they're a one-time five-minute setup.",
      },
      { type: "h2", text: "10. The annotations habit that will save you in 6 months" },
      {
        type: "p",
        text:
          "GA4 lacks a proper annotations feature (Universal Analytics had this, GA4 doesn't, which is one of the most-complained-about regressions), but the workaround is to keep a simple text file or Notion page where you log every meaningful change to your site, marketing, and product. New blog post published? Log it. Email blast sent? Log it. Pricing page redesigned? Log it. Three months from now, when you see a weird spike or dip and ask 'what was happening that week,' the file will save you an hour of guesswork.",
      },
      {
        type: "p",
        text:
          "The format doesn't matter. Date | What changed | Where. That's it. Bonus points if you tag each entry with which channel you'd expect it to move (e.g., 'email cadence change → Direct + Email', 'new pillar post → Organic Search'). After three months you'll start to notice patterns no dashboard can show you — the kinds of changes that move your business and the kinds that don't.",
      },
      { type: "h2", text: "11. Common mistakes to stop making" },
      {
        type: "p",
        text:
          "Three mistakes show up in almost every small-business GA4 audit, and all three are free to fix:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Looking at total Users instead of Sessions",
            body:
              "Users is a deduplicated count across the whole period and changes meaning when you change the date range. Sessions is a cleaner unit for week-over-week comparison because every session is exactly one visit.",
          },
          {
            label: "Not filtering out internal traffic",
            body:
              "If you, your team, or your developer hit the site frequently, you're polluting the data. Set up an internal IP filter under Admin → Data Streams → Configure tag settings → Define internal traffic. Five-minute job, lifetime payoff.",
          },
          {
            label: "Trusting Direct as 'people typing the URL'",
            body:
              "Most of Direct is actually broken attribution — links from native apps, email clients, and HTTPS-to-HTTP referrers that GA4 can't trace. If Direct moves, the cause is usually a campaign or attribution issue, not a sudden interest in typing your URL.",
          },
        ],
      },
      { type: "h2", text: "12. When to graduate from GA4 to something else" },
      {
        type: "p",
        text:
          "GA4 is free and powerful, but it's not the right primary tool for every business. If you spend more than 30 minutes per week trying to extract insights and you're still confused, that's a strong signal you need either a different tool or a layer on top. The 'different tool' answer is usually Plausible, Fathom, or Simple Analytics — privacy-friendly, single-dashboard, opinionated. The 'layer on top' answer is something like Plainly, which keeps your GA4 as the source of truth and generates the plain-English weekly summary on top of it. There's no single right answer; the wrong answer is to keep struggling for another year and assume it's your fault. GA4 is built for advertisers and large product teams, and if you're not one of those two, the friction you feel is real.",
      },
      { type: "h2", text: "The shortcut" },
      {
        type: "p",
        text:
          "This whole guide is what Plainly automates. We connect to your GA4 account, pull these exact reports, compare them week-over-week, and tell you in plain English what changed, why it likely happened, and what to try next. You read a friendly two-paragraph email on Monday morning instead of clicking through six dashboards. If you want to try it free, the demo dataset is one click away on the homepage.",
      },
    ],
  },
  {
    slug: "ga4-numbers-dont-match-other-tools",
    title: "Why your GA4 numbers don't match other tools (and which one to trust)",
    description:
      "Google Analytics, Shopify, Stripe, and your ad platforms all show different traffic and revenue numbers. Here's why, what's actually \"right,\" and how to reconcile them without losing your weekend.",
    date: "2026-02-08",
    readMinutes: 11,
    keywords: [
      "GA4 vs Shopify numbers",
      "Google Analytics doesn't match",
      "GA4 reporting discrepancies",
      "GA4 conversions don't match Stripe",
      "why are my analytics different",
      "GA4 vs Meta ads",
      "analytics reconciliation",
    ],
    author: { name: "Nicholas Steer", role: "Founder, Plainly" },
    content: [
      {
        type: "p",
        text:
          "You open GA4 on Monday and it says you got 1,200 sessions and 18 purchases. You open Shopify and it says 24 orders. You open Meta Ads and it claims 31 conversions came from your campaign. You open Stripe and the revenue figure is different from all three. Everyone is lying — except they're not. Each tool is technically correct from its own perspective. The reason none of them agree is that they're measuring different things with different rules, on different time windows, with different assumptions about what counts as 'you.' Here's the calm, definitive guide to why your numbers never match and how to figure out which one to trust for which question.",
      },
      { type: "h2", text: "1. Every analytics tool measures with a different yardstick" },
      {
        type: "p",
        text:
          "The first thing to internalise: there is no single 'right' number. There are only different definitions, each useful for different decisions. GA4 measures sessions and events triggered in a browser, blocked by cookie consent and ad blockers, attributed using its own attribution model. Shopify measures orders that actually completed checkout in its database. Stripe measures successful charges, which can lag Shopify by 1-3 days if customers pay with bank-redirect methods like iDEAL or SOFORT. Meta Ads measures conversions based on its pixel firing AND its own probabilistic modelling, with a default 7-day click + 1-day view attribution window. Even when two tools should agree, they almost never do because they're answering subtly different questions.",
      },
      {
        type: "p",
        text:
          "A useful mental model: think of each tool as a witness at a court case. None of them are lying, but each one only saw part of the event from their own angle. Your job isn't to find 'the witness who's right.' It's to know which witness to call for which question. Once you adopt that mindset, the discrepancies stop being upsetting and start being useful — you can triangulate.",
      },
      { type: "h2", text: "2. Why GA4 sessions are almost always lower than your raw page hits" },
      {
        type: "p",
        text:
          "If you've ever cross-checked GA4 against your hosting analytics (e.g., Cloudflare, Vercel, server logs), GA4 will look like it's missing 15-40% of visitors. That's not a bug. It's three compounding losses:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Cookie consent declines",
            body:
              "Anyone who hits 'Decline' on your cookie banner is invisible to GA4 unless you've configured Google Consent Mode v2 with cookieless pings. In the EU that's typically 30-50% of traffic; in the US it's a smaller but still significant slice depending on your audience.",
          },
          {
            label: "Ad blockers and privacy browsers",
            body:
              "Brave, Safari with iCloud Private Relay, Firefox with Enhanced Tracking Protection, and any user with uBlock Origin will silently block GA4 entirely. That's roughly 8-25% of US desktop traffic.",
          },
          {
            label: "Tag firing failures",
            body:
              "A slow site, a JavaScript error, or a misconfigured GTM container can cause the GA4 tag to fail on a slice of visits. You'll never see those in GA4 because they never reported themselves.",
          },
        ],
      },
      {
        type: "p",
        text:
          "Net result: GA4 typically captures 60-85% of true visits. If you want a baseline 'total reach' number, your hosting/CDN logs (or a privacy-friendly tool like Plausible) will be more accurate. But for behavioural analysis — what people DID once they arrived — GA4 is still the best free option, because it tracks events in much more detail than logs can.",
      },
      { type: "h2", text: "3. Why GA4 conversions never match Shopify or Stripe" },
      {
        type: "p",
        text:
          "This is the #1 reason e-commerce owners email their support: 'GA4 says 14 purchases, Shopify says 22 — what is going on?' Here's the unglamorous breakdown of where the missing 8 went:",
      },
      {
        type: "ul",
        items: [
          {
            label: "GA4 missed the page",
            body:
              "If your thank-you page redirects too fast, has a JavaScript error, or uses a checkout flow that bypasses your domain (Shopify Pay, Apple Pay, PayPal Express), GA4's purchase event may never fire even though Shopify recorded the order.",
          },
          {
            label: "The customer's browser blocked the tag",
            body:
              "Ad blockers and privacy browsers block GA4 on the thank-you page just like everywhere else. Conversions get dropped silently.",
          },
          {
            label: "Refunded, cancelled, or test orders",
            body:
              "Shopify counts orders as they happen. If the customer refunds within an hour and your GA4 setup deducts refunds (most don't), you'll see different numbers. Test orders by you or your team also pollute one but not the other.",
          },
          {
            label: "Cross-device journeys",
            body:
              "GA4 tries to stitch sessions across devices if users are logged in, but if your customer browses on mobile and checks out on desktop, they may count as two users in GA4 and one order in Shopify.",
          },
        ],
      },
      {
        type: "p",
        text:
          "Rule of thumb: Shopify and Stripe are the truth for revenue. GA4 is the truth for marketing attribution (which channel drove the visit that led to the order). Stop trying to reconcile total order counts between them. Reconcile the share each channel contributed instead, and you'll save yourself months of frustration.",
      },
      { type: "h2", text: "4. Why Meta Ads (or Google Ads) shows more conversions than GA4" },
      {
        type: "p",
        text:
          "Ad platforms almost always claim more conversions than GA4 attributes to them. Three reasons, in roughly the order they bite:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Attribution window mismatch",
            body:
              "Meta defaults to 7-day click + 1-day view. GA4's default attribution is data-driven within a 30-day click window for acquisition. If someone saw your Meta ad on day 1, didn't click but visited directly on day 5, Meta claims it; GA4 attributes it to Direct.",
          },
          {
            label: "Modelled conversions",
            body:
              "Since iOS 14.5, Meta and Google both fill in attribution gaps using machine-learning models because the pixel can't track everyone anymore. Roughly 15-30% of the conversions ad platforms claim are 'modelled' — i.e., guessed. They might be right, but they're not directly observed.",
          },
          {
            label: "View-through conversions",
            body:
              "Meta counts conversions when a user saw your ad and converted within 24 hours, even if they never clicked it. GA4 does not.",
          },
        ],
      },
      {
        type: "p",
        text:
          "The honest summary: ad platforms are incentivised to overclaim conversions (it makes ROAS look better, which keeps you spending). GA4 is incentivised to be conservative (Google's whole business model depends on the integrity of its analytics number). When the two disagree, the truth is usually somewhere between — but closer to GA4. As a default policy, trust GA4 for attribution and use ad platforms only for in-platform optimisation (Meta's algorithm needs the pixel data to optimise; you can't fix that, only live with it).",
      },
      { type: "h2", text: "5. Time-zone and date-cutoff traps" },
      {
        type: "p",
        text:
          "Half of all 'why don't my numbers match' tickets in agency-land turn out to be time-zone mismatches. GA4 reports in the property's configured time zone. Shopify reports in the shop's configured time zone. Meta reports in the ad-account's time zone. Stripe reports in UTC. If your property is set to America/Los_Angeles but your Shopify is set to America/New_York and your Meta account is set to UTC, a 'day' is three different windows across the three tools. Orders placed at 11pm Eastern fall on different days in each tool's report.",
      },
      {
        type: "p",
        text:
          "Fix this once and forever: pick one timezone (your business timezone) and set every tool to use it. Yes, this means digging through Shopify settings, GA4 admin, Meta account settings, Stripe (read-only — they report in UTC and you adjust at query time), Google Ads, and any BI tool you use. It takes 30 minutes and removes a permanent class of confusion. Future-you will thank you.",
      },
      { type: "h2", text: "6. Which number to trust for which question" },
      {
        type: "p",
        text:
          "Memorise this little decision tree and you'll never have a reconciliation crisis again:",
      },
      {
        type: "ul",
        items: [
          {
            label: "How much money did I actually make?",
            body:
              "Stripe (or your payment processor). Always. This is the only number your accountant cares about.",
          },
          {
            label: "How many orders did I get?",
            body:
              "Shopify (or your e-commerce platform). It's the source of truth for orders because every order exists in its database by definition.",
          },
          {
            label: "Which marketing channel drove the most revenue?",
            body:
              "GA4 — but only the channels report (Acquisition → Traffic acquisition), and only if you've set up GA4 ecommerce tracking correctly. Treat the absolute numbers as approximate and focus on the share each channel contributed.",
          },
          {
            label: "Did my Meta campaign work?",
            body:
              "Meta Ads Manager for in-platform optimisation, GA4 for ground truth. If they disagree by more than 30%, trust GA4.",
          },
          {
            label: "How many total people visited my site?",
            body:
              "Your hosting/CDN logs (or Plausible/Fathom if you've installed one). GA4 will undercount by 15-40%.",
          },
          {
            label: "Which page is the strongest part of my funnel?",
            body:
              "GA4 — Engagement → Landing page → engagement time + key event rate. No other tool has this data.",
          },
        ],
      },
      { type: "h2", text: "7. The reconciliation ritual that actually scales" },
      {
        type: "p",
        text:
          "Once a month, do a 20-minute reconciliation ritual instead of trying to perfectly match every number every day. Open Stripe, Shopify, GA4, and your ad platforms side by side. Write down the headline number from each for the same date range (use the shared business timezone). Note the gaps. Don't try to fix them — just SEE the consistent ratios. Once you know that GA4 captures roughly 78% of Shopify's orders, you can apply that ratio mentally whenever you look at GA4 in isolation. After three months of this, you'll have a calibrated mental model that's more useful than any spreadsheet that tries to reconcile every transaction.",
      },
      {
        type: "p",
        text:
          "Document the ratios in a single page. 'Shopify : GA4 ratio for January = 1.28×; February = 1.24×; March = 1.31×.' If a ratio suddenly spikes — say to 1.6× — that's signal worth investigating (probably your GA4 tag broke for a slice of traffic). If it stays in a narrow range, you can stop worrying about it and get back to building the business.",
      },
      {
        type: "quote",
        text:
          "Stop trying to make every tool agree. Decide which tool answers which question, then use it. The discrepancies aren't bugs — they're the cost of getting independent perspectives.",
      },
      { type: "h2", text: "8. When the gap actually does signal a problem" },
      {
        type: "p",
        text:
          "Most discrepancies are normal noise. But a few patterns are genuine red flags worth a deeper look:",
      },
      {
        type: "ul",
        items: [
          {
            label: "GA4 suddenly captures <50% of Shopify orders",
            body:
              "That's a tag or thank-you-page issue. Open the live site in incognito, complete a test order (use a 100% discount code), and watch DevTools → Network for the purchase event. If it doesn't fire, your tag broke.",
          },
          {
            label: "Meta claims 3× more conversions than GA4",
            body:
              "Usually means Meta's modelling is in overdrive because the pixel coverage dropped. Check your Conversions API setup; you may have lost server-side events when a developer redeployed.",
          },
          {
            label: "Stripe revenue exceeds Shopify revenue",
            body:
              "Probably manual invoices or subscription payments that bypass the storefront. Not a problem, just incomplete picture if you're using Shopify's reporting as ground truth.",
          },
          {
            label: "Hosting logs show 5× more visits than GA4",
            body:
              "Either bot traffic (logs see bots, GA4 filters most of them) or a serious tag/consent problem. Sample-check the hosting logs to see if it's bot UA strings or real Chrome/Safari sessions.",
          },
        ],
      },
      { type: "h2", text: "9. The honest take" },
      {
        type: "p",
        text:
          "There is no analytics utopia where every number agrees. The era of perfect first-party tracking ended with GDPR, iOS 14.5, and the slow death of third-party cookies. What you have instead is a constellation of imperfect tools whose disagreements, taken together, tell a more useful story than any single 'perfect' tool ever could. Embrace that. Pick your source of truth for each question, document the ratios, and stop spending Sundays trying to make charts match. The time you save is better spent on the things that actually move the business.",
      },
      { type: "h2", text: "10. A worked example: the 'where did 8 orders go' diagnosis" },
      {
        type: "p",
        text:
          "Here's how this looks in practice. A small DTC brand opens Shopify on Monday: 142 orders last week. They open GA4: 116 'purchase' events. That's 26 orders missing — roughly 18% — and the owner panics. They spend 90 minutes reading articles like this one (hi) before running a structured check. Step 1: count how many were Shop Pay or Apple Pay checkouts (those bypass the storefront and often skip the GA4 purchase event). Shopify analytics shows 19 of the 142 were Shop Pay. That accounts for most of the gap. Step 2: load the live thank-you page in Brave incognito, complete a test order with a 100% discount. The purchase event does NOT fire — Brave is blocking GA4. So a slice of the remaining 7 missing orders are from privacy-browser customers. Total time: 22 minutes. Diagnosis: nothing is broken. Action: install the Shopify Conversions API integration to recover the Shop Pay orders server-side. Estimated GA4 capture rate after fix: 95% instead of 82%.",
      },
      {
        type: "p",
        text:
          "Notice what the diagnosis is NOT: 'GA4 is broken,' 'Shopify is lying,' 'the SEO agency is cooking the books.' Most reconciliation panic comes from skipping the structured walkthrough and jumping straight to a story. The story is usually wrong. The structured walkthrough takes 20-30 minutes and almost always lands on a boring, fixable explanation — usually a known checkout-flow quirk or a privacy-browser slice. Write down the ratio after the fix, sleep well, move on.",
      },
      { type: "h2", text: "Or, just let Plainly do the reconciling for you" },
      {
        type: "p",
        text:
          "Plainly's weekly readout pulls the GA4 number, the channel-level attribution, and the per-page engagement and tells you in plain English which one moved this week, by how much, and what likely caused it. We can't reconcile your Stripe and Meta numbers (yet), but we'll stop the weekly 'why don't these match' panic by giving you the one number to actually act on. Try the free demo from the homepage — no card required.",
      },
    ],
  },
  {
    slug: "ga4-for-small-business-owners-getting-started",
    title: "GA4 for small business owners: the complete getting-started guide",
    description:
      "No jargon, no agency talk — just the exact setup, settings, and first-week routine a small business owner needs to get useful answers out of Google Analytics 4.",
    date: "2026-02-01",
    readMinutes: 12,
    keywords: [
      "GA4 for small business",
      "Google Analytics for small business",
      "how to use GA4",
      "GA4 setup guide",
      "GA4 beginner guide",
      "Google Analytics 4 for beginners",
    ],
    author: { name: "Nicholas Steer", role: "Founder, Plainly" },
    content: [
      {
        type: "p",
        text:
          "If you run a small business, Google Analytics 4 looks like it was designed by aliens. It was actually built for product teams at companies that have data analysts on staff — not for the boutique owner who just wants to know if Instagram is sending real customers. The good news: 90% of GA4 is irrelevant to you, and the 10% that matters can be set up in an afternoon and reviewed in five minutes a week. Here's the no-jargon, no-agency-fluff version of how to do exactly that.",
      },
      { type: "h2", text: "1. First, decide if you even need GA4" },
      {
        type: "p",
        text:
          "Be honest: do you need website analytics at all? If you have less than 50 visitors a day and you're using your site as a digital business card, you probably don't. Most decisions at that stage are made on instinct and customer conversations, not data. But if you're spending money on ads, running campaigns, writing blog posts for SEO, or making content decisions about which products to feature, then yes — you need to know what's happening on your site, and GA4 is the free option that everyone else uses.",
      },
      {
        type: "p",
        text:
          "If you've decided GA4 is overkill but you do want something, look at Plausible or Fathom. Both are 30 dollars a month, privacy-friendly, and have a single dashboard that fits on one screen. No event tracking, no funnels, no machine learning — just sessions, sources, and pages. For 80% of solo operators, that's actually the right tool. The rest of this guide is for the 20% who genuinely need GA4's deeper reporting.",
      },
      { type: "h2", text: "2. Install GA4 (the 15-minute version)" },
      {
        type: "p",
        text:
          "There are essentially three ways to install GA4 on your site, depending on what platform you're on:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Shopify, Squarespace, Wix, Webflow",
            body:
              "All of these have a built-in GA4 integration. Go to your store admin → settings → integrations (or analytics) → paste your GA4 Measurement ID (starts with G-). Done. You don't need GTM. Don't make this harder than it is.",
          },
          {
            label: "WordPress",
            body:
              "Install the 'Site Kit by Google' plugin, sign in with the same Google account that owns your GA4, and follow the wizard. Takes about 4 minutes. Skip plugins that ask you to install GTM — for a small business you almost never need it.",
          },
          {
            label: "Custom site (you have a developer)",
            body:
              "Send them the gtag.js snippet from GA4 Admin → Data Streams → Web stream → Tagging Instructions → 'View tag instructions' → Install manually. Tell them to put it in the <head> of every page, not just the homepage.",
          },
        ],
      },
      {
        type: "p",
        text:
          "Once installed, verify it works. Open your live site in an incognito tab, then go back to GA4 → Reports → Realtime. Within 30 seconds you should see yourself in the report. If you don't, the tag isn't firing — do not proceed until you fix it. There is nothing more depressing than discovering three months in that all your data has been zero because of a missing tag.",
      },
      { type: "h2", text: "3. Set up the four settings that 95% of small businesses skip" },
      {
        type: "p",
        text:
          "Out of the box, GA4 will give you data, but the data will be polluted by your own visits, miscategorised by source, and impossible to compare to other tools because of timezone confusion. Fix that on day one with these four settings:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Set your time zone",
            body:
              "Admin → Property settings → Property details → Reporting time zone. Match it to your Shopify, your bank, and your real-life calendar. Do this NOW — changing it later resets your day-of-week comparisons.",
          },
          {
            label: "Set your currency",
            body:
              "Same screen. If you sell internationally, pick the one your accountant uses, not the one most customers spend in. Otherwise every report will show converted figures that confuse you.",
          },
          {
            label: "Filter out your internal traffic",
            body:
              "Admin → Data Streams → web stream → Configure tag settings → Define internal traffic → Add your home IP. Then Admin → Data filters → activate the 'Internal Traffic' filter. This removes your own visits forever.",
          },
          {
            label: "Set 'cross-domain tracking' if you use a third-party checkout",
            body:
              "If your checkout lives on a different subdomain (e.g., checkout.yourstore.com) or a Shopify Pay-hosted domain, add it under Data Streams → Configure tag settings → Configure your domains. Otherwise you'll see traffic 'jumping' to that domain as Direct.",
          },
        ],
      },
      { type: "h2", text: "4. Define what 'success' means before you start measuring" },
      {
        type: "p",
        text:
          "This is the single most important step, and almost everyone skips it because it doesn't feel like analytics work. Sit down for 10 minutes and write the answer to: 'What is the one thing a visitor can do on my site that means this visit was successful?' For most small businesses it's one of three things — buy a product, book a call, or sign up for the newsletter.",
      },
      {
        type: "p",
        text:
          "Then go to GA4 Admin → Events → Mark the matching event as a Key Event (formerly 'Conversion'). If you sell online, that's 'purchase'. If you're service-based, it's usually 'generate_lead' or 'contact'. If your goal is email subscribers, you'll need to fire a custom event when someone signs up — your email tool (Mailchimp, Klaviyo, ConvertKit) usually has a one-click GA4 integration. Once a Key Event is set, every report will start showing 'conversions' alongside sessions and users, and you'll finally be able to tell the difference between 'traffic that visited' and 'traffic that mattered.'",
      },
      { type: "h2", text: "5. The five reports a small business owner actually needs" },
      {
        type: "p",
        text:
          "GA4 has dozens of reports. You need five. Bookmark them on day one and ignore everything else for the first 90 days:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Reports → Acquisition → Traffic acquisition",
            body:
              "Where your visitors came from. Answers 'is my SEO working' and 'is Instagram paying off.'",
          },
          {
            label: "Reports → Engagement → Pages and screens",
            body:
              "What people actually read. Answers 'which blog post is my best one.'",
          },
          {
            label: "Reports → Engagement → Landing page",
            body:
              "Where people first arrived. Tells you which page is your real front door (hint: usually not your homepage).",
          },
          {
            label: "Reports → Engagement → Events → conversions (if you're online)",
            body:
              "Which Key Events fired, attributed to which channels. The main commercial report.",
          },
          {
            label: "Reports → User → Demographic details",
            body:
              "If you have ad spend, this tells you the actual geography and devices of your buyers. Skip if you don't run ads.",
          },
        ],
      },
      { type: "h2", text: "6. The five-minute Monday routine" },
      {
        type: "p",
        text:
          "Once GA4 is set up correctly, you only need a 5-minute weekly review to get 80% of the value. The routine never changes; the discipline of doing it weekly does:",
      },
      {
        type: "ul",
        items: [
          { label: "Step 1 — Set the date range", body: "Last 7 days, with the comparison toggle enabled vs. the previous 7 days." },
          { label: "Step 2 — Open Traffic acquisition", body: "Note which channel moved most (up or down) and by how much." },
          { label: "Step 3 — Open Landing page", body: "Look at the top 3 pages. Which engaged-sessions number jumped or fell?" },
          { label: "Step 4 — Open Pages and screens, sort by engagement time", body: "Quickly spot pages with high views but low engagement — those need a refresh." },
          { label: "Step 5 — Write down one action", body: "One thing you'll change this week based on what you saw. One. Not five. Five is the same as zero." },
        ],
      },
      {
        type: "p",
        text:
          "Do this every Monday for 8 weeks and you'll have a calibrated sense of what 'normal' looks like for your site — without ever needing to read a single analytics tutorial. The compounding gain from this habit dwarfs anything fancy you could learn in the same time.",
      },
      { type: "h2", text: "7. Things small business owners worry about (that don't matter)" },
      {
        type: "p",
        text:
          "Most of the time you spend stressed about GA4 is wasted on things that don't actually affect any decision you'll ever make. Permission to ignore the following:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Bounce rate",
            body:
              "GA4 redefined it; the new definition is closer to 'engagement rate inverted.' A high bounce on a single-page blog post is FINE — they read it and left.",
          },
          {
            label: "Realtime reports",
            body:
              "Fun the first week, useless for any decision after that. Close the tab.",
          },
          {
            label: "Average session duration",
            body:
              "Includes the time someone left a tab open while they made dinner. Use engagement time instead.",
          },
          {
            label: "User vs. Active User counts not matching",
            body:
              "Different definitions, both right. Pick Active Users and never look at the other.",
          },
          {
            label: "'My traffic dropped this weekend!'",
            body:
              "Most small-business sites lose 30-40% of traffic on weekends. Compare same-day-of-week to same-day-of-week, not 'today vs. yesterday.'",
          },
        ],
      },
      { type: "h2", text: "8. When to involve someone else" },
      {
        type: "p",
        text:
          "You don't need an analytics consultant for the basics. But there are three moments when it's worth paying someone:",
      },
      {
        type: "ul",
        items: [
          {
            label: "When you're spending more than $2,000/month on ads",
            body:
              "At that point, a properly-configured GA4 + ad-platform setup can pay for itself in the first month. Hire someone for a single 2-hour audit, not an ongoing retainer.",
          },
          {
            label: "When you want server-side tracking",
            body:
              "If you're losing 30%+ of conversions to ad blockers, server-side tracking via the Conversions API or Stape can recover most of it. Requires a developer and a half-day setup. Worth it once you have meaningful ad spend.",
          },
          {
            label: "When you're scaling past 10,000 sessions/month",
            body:
              "At that volume the 10-million-row table limit in GA4's free tier starts to matter for advanced reports. You may need Looker Studio + BigQuery to slice the data the way you want.",
          },
        ],
      },
      { type: "h2", text: "9. The decision-making mindset" },
      {
        type: "p",
        text:
          "The single biggest mistake small business owners make with analytics is treating it as a report card instead of a decision-support tool. A number is not 'good' or 'bad' in isolation; it's only useful relative to a specific decision you're about to make. 'Sessions went up 12%' is meaningless. 'Sessions went up 12% AND it was entirely from organic search on the pricing page, which means the SEO push worked AND I should keep going' is useful. Always force yourself to translate the numbers into a sentence that ends with a specific next action.",
      },
      {
        type: "p",
        text:
          "If you can't translate a metric into an action, ignore the metric. There are dozens of numbers in GA4 that exist purely because Google's product team needed them for their own internal reasons. You don't owe them your attention. Pick the 4-5 that drive your actions and let the rest pile up unused. That's the secret to actually using analytics instead of being intimidated by them.",
      },
      {
        type: "quote",
        text:
          "GA4 is overwhelming on purpose. The trick is to ignore 90% of it on purpose, too.",
      },
      { type: "h2", text: "10. The shortcut for everything in this guide" },
      {
        type: "p",
        text:
          "Everything above describes what a thoughtful small-business owner SHOULD do with GA4 each week. Most don't, because the friction is too high and the payoff doesn't feel immediate. That's exactly the problem Plainly solves. We pull the five reports above, compare them week-over-week, surface the one channel or page that actually moved, and write you a plain-English paragraph with one concrete next step. It arrives in your inbox every Monday at 8am. You read it with your coffee. No GA4 tab required. If the routine above sounds great in theory but you know yourself well enough to know you won't actually do it, try the free demo from the homepage — no credit card required.",
      },
      { type: "h2", text: "11. Common questions small business owners actually ask" },
      {
        type: "p",
        text:
          "A few quick answers to questions I get over and over from solo operators getting started with GA4:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Do I need a separate GA4 property for my staging site?",
            body:
              "Yes. Create a second property called '[Yourbrand] Staging' and put its Measurement ID on the staging environment only. Otherwise your test traffic pollutes the real property and your real metrics get noisy.",
          },
          {
            label: "Should I use Google Tag Manager (GTM) or just plain gtag.js?",
            body:
              "If you're on Shopify/Squarespace/Wix/Webflow/WordPress with Site Kit — use the native integration. Skip GTM. GTM is overkill for a single GA4 tag and adds a whole layer of complexity (and another thing that can break).",
          },
          {
            label: "How long should I wait before drawing conclusions from a new GA4 install?",
            body:
              "Minimum 14 days for week-over-week comparisons, ideally 30 days. The first week is always weird because cookie consent, sampling, and audience identifiers stabilise gradually.",
          },
          {
            label: "Does GA4 cost money?",
            body:
              "No, the GA4 standard tier is free. There is a paid 'GA4 360' for enterprises (starts around $50k/year) — you don't need it. If anyone is trying to sell you GA4 360 for a small business, walk away.",
          },
          {
            label: "Can I delete or merge old data when I switch businesses?",
            body:
              "You can delete properties (and all their data) under Admin → Property settings → Move to trash, but you can't merge two GA4 properties. If you're rebranding, just create a new property and keep the old one read-only for historical reference.",
          },
        ],
      },
      {
        type: "p",
        text:
          "If any of these answers raised a follow-up question, the GA4 community on Reddit (r/analytics, r/GoogleAnalytics) is unusually helpful for small-business questions. The Google official documentation is written for product managers at large companies and will make you feel dumber than you are. It's not you — it's them.",
      },
    ],
  },
  {
    slug: "whats-new-in-ga4-2026",
    title: "What's new in Google Analytics 4 (2026): the 7 changes that actually matter",
    description:
      "A plain-English roundup of the biggest Google Analytics 4 changes shipped in the last 18 months — what each one means, who it affects, and what (if anything) you need to do this week.",
    date: "2026-02-26",
    readMinutes: 11,
    keywords: [
      "GA4 changes 2026",
      "what's new in GA4",
      "Google Analytics news 2026",
      "GA4 updates",
      "Google Analytics 4 changes",
      "GA4 key events",
      "Consent Mode v2",
      "GA4 attribution changes",
    ],
    author: { name: "Nicholas Steer", role: "Founder, Plainly" },
    content: [
      {
        type: "p",
        text:
          "Google has shipped more changes to Google Analytics 4 in the last 18 months than in the entire run of Universal Analytics before it. Most of those changes were tiny tweaks that don't affect anyone. A few of them genuinely change how you should be using the tool, and one of them probably broke your remarketing without you noticing. Here's the plain-English roundup of the seven changes that actually matter for small businesses and the agencies who help them — what each one means, who it affects, and what you need to do this week.",
      },
      { type: "h2", text: "1. Universal Analytics is officially, irreversibly gone" },
      {
        type: "p",
        text:
          "Universal Analytics stopped collecting new data in July 2023, but the historical data stayed accessible inside the old UA properties for another year. That ran out on July 1, 2024, when Google deleted all UA data permanently. There is no recovery option, no archive, no \"please bring it back.\" If you didn't export your UA historical data to BigQuery, CSV, or a third-party warehouse before that date, those years of behavioural history are gone for good.",
      },
      {
        type: "p",
        text:
          "What this means in early 2026: most businesses still doing year-over-year comparisons against the UA era are comparing apples to oranges. GA4 measures sessions, users, and conversions with different definitions than UA did. If your weekly report still references \"sessions vs last year\" using exported UA numbers, you're comparing two different yardsticks — and the comparison is almost always misleading. The honest baseline year is now whichever year you got GA4 running properly. For most small businesses that's 2024 or 2025.",
      },
      {
        type: "p",
        text:
          "Action item: stop comparing your 2026 GA4 numbers against your UA numbers. Either pick GA4's first full year as the baseline, or work in shorter (90-day, 180-day) rolling comparisons until you accumulate enough comparable GA4 history. Your future-self will trust the data more if you stop trying to bridge the old and new world.",
      },
      { type: "h2", text: "2. \"Conversions\" got renamed to \"Key Events\" (and the meaning changed)" },
      {
        type: "p",
        text:
          "In late 2024 Google renamed every \"conversion\" in GA4 to \"key event,\" and the distinction is not just cosmetic. A \"key event\" in GA4 is now any event you've marked as business-important — purchases, form submits, scroll-depth milestones, anything. The word \"conversion\" is now reserved for events imported into Google Ads, where it has a stricter advertising-specific meaning. The two terms are no longer synonymous.",
      },
      {
        type: "p",
        text:
          "What this means: every old tutorial you find online that says \"set up a conversion in GA4\" is using vocabulary that no longer matches the interface. The clicks are the same — Admin → Events → toggle on \"Mark as key event\" — but everything is labelled differently and the columns in your reports moved. Tutorials that haven't been updated since 2024 will leave you hunting for buttons that have been renamed.",
      },
      {
        type: "p",
        text:
          "Action item: open Admin → Events in your GA4 property and confirm your business-critical events (purchase, generate_lead, contact, signup) are marked as key events. The toggle is now called \"Mark as key event\" — if you don't see your critical event in the list, you may need to create it as a custom event first.",
      },
      { type: "h2", text: "3. Google Consent Mode v2 is mandatory for EU advertisers (and almost certainly affecting your tag coverage)" },
      {
        type: "p",
        text:
          "Consent Mode v2 became required in March 2024 for any advertiser using Google Ads remarketing or measurement features on EU/EEA traffic. The short version: if a visitor declines analytics cookies, your GA4 tag must either suppress the request entirely OR send a cookieless \"ping\" with stripped identifiers. The version-2 update introduced two new consent signals (ad_user_data and ad_personalization) that are now required separately from analytics_storage.",
      },
      {
        type: "p",
        text:
          "What this means in practice: if your cookie banner hasn't been updated since 2023, your remarketing audiences in Google Ads have likely been quietly shrinking because Google is rejecting non-compliant data. We've seen Google Ads audiences drop 40-60% in the months after Consent Mode v2 enforcement kicked in for sites that didn't update their banner. The traffic didn't change — Google's willingness to accept the data did.",
      },
      {
        type: "p",
        text:
          "Action item: ask your cookie-consent vendor (Iubenda, Termly, CookieYes, OneTrust, Cookiebot) whether your banner is sending all four Consent Mode v2 signals — analytics_storage, ad_storage, ad_user_data, ad_personalization. If the answer isn't an immediate yes, update the banner today. The fix is usually a single toggle in your consent tool. This is the most common silent data problem we see in 2026.",
      },
      { type: "h2", text: "4. AI-generated insights now appear in every GA4 property" },
      {
        type: "p",
        text:
          "Google has rolled out an automated \"Insights\" panel in every GA4 property, where their own AI surfaces anomalies and trends without you asking. It lives on the Home tab and updates daily, calling out things like \"Organic Search traffic increased 38% week-over-week\" or \"Pageviews for /pricing dropped sharply on Tuesday.\" The feature is free and on by default.",
      },
      {
        type: "p",
        text:
          "What this means: Google has finally admitted that 95% of GA4 users don't actually use GA4 — they just want a Monday-morning summary. The Insights panel is their first real attempt to deliver that, and for the basic \"what changed this week\" question, it's surprisingly useful. The limitations: the panel doesn't explain WHY anything moved, the language is sterile, and the anomalies don't link to specific suggested actions. You still have to interpret the changes yourself. (This is also exactly the gap Plainly was built to fill — we connect to the same data, but we write the WHY in plain English and tell you what to try next, automatically by email every Monday.)",
      },
      {
        type: "p",
        text:
          "Action item: log into GA4, click Home, and see if there's an Insights panel on the right side of your dashboard. If yes, glance at it daily for two weeks. You'll quickly learn whether Google's own auto-detection is enough for you, or whether you need a layer that translates the anomalies into actions.",
      },
      { type: "h2", text: "5. The Reports library lets you finally customise the navigation" },
      {
        type: "p",
        text:
          "One of the most common complaints about GA4 since launch was that the left-hand navigation was bloated and impossible to clean up — every property looked the same and most of the menu items were irrelevant for any given business. Google quietly fixed this in 2024 by shipping the Reports → Library editor: a way for any property editor or owner to add, remove, hide, and reorder every report in the navigation.",
      },
      {
        type: "p",
        text:
          "What this means: you can finally hide \"User attributes,\" \"Tech details,\" \"App developer\" and all the other reports your team will never open, and pin the 3-4 reports you actually use to the top. The five reports we recommend pinning for a small business: Traffic acquisition, Landing page, Pages and screens, Events, and Key events. Everything else can go in a \"More\" collection or be hidden entirely.",
      },
      {
        type: "p",
        text:
          "Action item: in GA4, click Reports → Library (bottom of the left nav) → Edit the default Collection. Move your top reports to the top, hide everything you don't use. It's a 10-minute job and it makes every subsequent GA4 visit dramatically less overwhelming.",
      },
      { type: "h2", text: "6. Custom channel groups are now widely available" },
      {
        type: "p",
        text:
          "GA4 originally forced everyone to use Google's standard channel definitions — \"Organic Social,\" \"Paid Search,\" \"Direct,\" etc. — with no way to override them. That changed in 2024 when Google rolled out custom channel groups to all standard (free) properties. You can now define your own rules for which source/medium combinations roll up into which channel name.",
      },
      {
        type: "p",
        text:
          "What this means: if you've ever been frustrated that traffic from Substack shows up as \"Referral\" instead of \"Email,\" or that your specific newsletter campaigns get lumped in with random press mentions, you can now fix it. Admin → Channel groups → Create new channel group lets you define rules like \"if source contains 'substack' OR medium = 'newsletter', call it Email.\" The custom group lives alongside the default one, so your historical reporting stays consistent.",
      },
      {
        type: "p",
        text:
          "Action item: only worth doing if your reports are actually misleading you because of mis-grouped traffic. If 90% of your traffic falls into the standard channels correctly, leave it alone. If you're constantly explaining away the same mis-classifications to clients or your team, build a custom channel group and switch the reports to use it.",
      },
      { type: "h2", text: "7. Attribution settings changed — you might be measuring two different things" },
      {
        type: "p",
        text:
          "In 2024 Google rolled out a major change to default attribution models in GA4: \"Data-driven attribution\" became the default for new properties, replacing \"Last click\" which was the silent default for years. This change applies to acquisition reports, conversion reports, and what gets pushed back to Google Ads. The catch: properties created before the change kept their old setting unless someone manually updated it.",
      },
      {
        type: "p",
        text:
          "What this means: if you compare attribution numbers between two GA4 properties — say, yours and a client's — they may be using different attribution models without anyone realising it. \"Data-driven\" tends to credit upper-funnel channels (Social, Display) more generously than \"Last click\" does, so the same exact customer journey can look different depending on which model is active. This causes endless confusion when an agency reports different conversion numbers than the brand's own GA4 dashboard.",
      },
      {
        type: "p",
        text:
          "Action item: open Admin → Attribution settings and check which model is set. For most small businesses we recommend explicitly choosing \"Data-driven\" (it's free and more accurate) but knowing which one you've picked. If you switch, expect a 10-30% re-distribution of credit across channels — that's the model adjusting, not your actual marketing changing.",
      },
      {
        type: "quote",
        text:
          "Every quarter, Google ships changes that subtly redefine what your numbers mean. If you don't audit the settings annually, you're slowly drifting into reports that measure something different than they did last year.",
      },
      { type: "h2", text: "What didn't make the list (but might still matter to you)" },
      {
        type: "p",
        text:
          "A handful of smaller changes shipped that we deliberately left off the headline list because they only matter to a subset of users:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Increased free BigQuery export quota",
            body:
              "Google raised the free daily export limit for standard GA4 properties — useful only if you're already piping GA4 into BigQuery for custom reporting. If you've never opened BigQuery, ignore.",
          },
          {
            label: "Subproperties and roll-up properties became more accessible",
            body:
              "Useful for agencies and multi-brand businesses, still gated behind GA4 360 ($50k+/year). Not relevant for solo operators.",
          },
          {
            label: "Cross-channel data-driven attribution lookback window changed",
            body:
              "Default lookback for paid conversions adjusted from 30 days to 90 days. Mostly affects ad-heavy businesses comparing year-over-year ROAS.",
          },
          {
            label: "New 'Modeled conversions' transparency",
            body:
              "GA4 now flags which conversions are observed vs. modelled. Good if you've ever wondered why GA4 numbers seem 'too clean' — modelled conversions are an estimate, not a count.",
          },
        ],
      },
      { type: "h2", text: "The 20-minute 2026 audit checklist" },
      {
        type: "p",
        text:
          "If you read nothing else above, run through this checklist once and you'll be ahead of 90% of small businesses on GA4 hygiene in 2026:",
      },
      {
        type: "ul",
        items: [
          { label: "1. Confirm Consent Mode v2 is sending all 4 signals", body: "Most common silent data problem this year. Ask your cookie-consent vendor." },
          { label: "2. Check Admin → Attribution settings", body: "Pick 'Data-driven' explicitly. Document the choice." },
          { label: "3. Open Admin → Events", body: "Mark your business-critical events as Key events." },
          { label: "4. Trim the Reports library", body: "Pin the 5 reports you actually use, hide the rest." },
          { label: "5. Confirm internal-traffic filter is active", body: "Filter out your own visits before they pollute the data." },
          { label: "6. Time zone matches your business", body: "If you compare GA4 to Shopify or Stripe, all three must agree on what 'a day' is." },
          { label: "7. Glance at the Insights panel weekly", body: "Free anomaly detection; decide whether it's enough for you or if you need a deeper layer." },
        ],
      },
      {
        type: "p",
        text:
          "Two of these (Consent Mode v2 and attribution settings) are the most-overlooked problems we see in 2026 audits, and both can quietly distort your numbers by 20-40% without anyone noticing. Fixing them takes 20 minutes combined and pays for itself the first time you make a decision based on cleaner data.",
      },
      { type: "h2", text: "The bigger trend: GA4 is finally maturing" },
      {
        type: "p",
        text:
          "Three years after Universal Analytics shut down, GA4 is finally maturing into something a small business can actually use without an analyst. The Insights panel, the Reports library, custom channel groups, and clearer attribution settings collectively close most of the \"GA4 is broken\" complaints from 2022-2023. The remaining gap — translating the data into plain-English narrative and recommendations — is what tools like Plainly handle on top.",
      },
      {
        type: "p",
        text:
          "Looking ahead: in 2026 Google is heavily betting on AI-assisted analytics. Expect the Insights panel to get smarter, expect more LLM-generated summaries inside GA4 itself, and expect the boundary between \"raw analytics\" and \"AI-summarised insights\" to keep blurring. The smart move for small businesses is to spend less time learning GA4's interface and more time defining which decisions you actually want analytics to answer — then pick whichever tool (GA4's Insights, Plainly, a consultant) gets you those answers in the format that fits your weekly workflow.",
      },
      { type: "h2", text: "Or, just let Plainly summarise the changes for you each week" },
      {
        type: "p",
        text:
          "Plainly reads your GA4 data every Monday and writes you a plain-English summary of what changed week-over-week, what likely caused it, and what to try next — using the exact channel and page-level breakdowns covered above. No dashboards to learn, no settings to babysit, no '7 changes that actually matter' to memorise. We do the interpreting; you make the decisions. Try the free demo from the homepage — no credit card required.",
      },
    ],
  },
  // -------------------------------------------------------------------------
  // Iter 13 — three new search-driven posts targeting high-volume long-tail
  // queries that don't have a strong existing page on the site. Each post is
  // a single-topic deep-dive (one metric or one symptom) so it can rank for
  // a tight keyword cluster without competing with our existing posts.
  // -------------------------------------------------------------------------
  {
    slug: "what-does-engagement-rate-mean-in-google-analytics",
    title: "What does Engagement Rate actually mean in Google Analytics? (GA4, plain English)",
    description:
      "Engagement rate in Google Analytics 4 is the percentage of sessions that lasted longer than 10 seconds, viewed 2+ pages, or triggered a key event. It's calculated as engaged sessions ÷ total sessions × 100, replaced bounce rate as GA4's headline quality metric in 2023, and a 'good' rate is 40–65% for most small businesses.",
    date: "2026-04-19",
    readMinutes: 9,
    keywords: [
      "engagement rate google analytics",
      "what is engagement rate ga4",
      "ga4 engagement rate explained",
      "what does engagement rate mean",
      "google analytics engagement rate",
      "engagement rate vs bounce rate",
      "good engagement rate ga4",
    ],
    author: { name: "Nicholas Steer", role: "Founder, Plainly" },
    content: [
      {
        type: "p",
        text:
          "Engagement rate is the metric most small-business owners encounter first in GA4 — and the one they trust the least. It sits prominently at the top of every report, it changes every week, and Google's own documentation describes it in a single sentence that conveniently leaves out the bits that actually matter. The result is a number people quote without understanding, ignore when it falls, and panic about when it spikes.",
      },
      {
        type: "p",
        text:
          "This guide unpacks what engagement rate actually counts in 2026, why it replaced bounce rate as GA4's headline quality metric, what a 'normal' number looks like for your kind of site, and the three real-world reasons it usually swings week-over-week. By the end you'll be able to read it like you read your bank balance — at a glance, with no anxiety.",
      },
      { type: "h2", text: "The one-sentence definition (and the four-word footnote)" },
      {
        type: "p",
        text:
          "Engagement rate is the share of sessions that GA4 decided were 'engaged'. The footnote is: GA4 decides. A session counts as engaged if it meets at least one of three criteria — none of which require any input from you.",
      },
      {
        type: "ul",
        items: [
          {
            label: "Lasted longer than 10 seconds",
            body:
              "The default threshold is 10 seconds. You can change this in GA4 → Admin → Data Streams → Web → Configure tag settings → Adjust session timeout, but 99% of sites leave it on the default.",
          },
          {
            label: "Had a conversion event",
            body:
              "If the user triggered any event you've marked as a 'key event' (the new name for conversions), their session is engaged regardless of how briefly they were there. A 4-second visit that ends in a $200 purchase counts as engaged.",
          },
          {
            label: "Viewed 2 or more pages or screens",
            body:
              "Two or more page_view events in a single session. Refreshing the same page doesn't count — GA4 deduplicates by URL within a session in most setups.",
          },
        ],
      },
      {
        type: "p",
        text:
          "Engagement rate is then expressed as a percentage: engaged sessions ÷ total sessions × 100. If 100 people visited and 60 of them stayed longer than 10 seconds, viewed 2+ pages, or converted, your engagement rate is 60%.",
      },
      { type: "h2", text: "Why engagement rate replaced bounce rate" },
      {
        type: "p",
        text:
          "In the old Universal Analytics, bounce rate measured 'visits that left after looking at one page'. It was simple, intuitive, and deeply flawed. A user could land on your contact page, find your phone number, call you, and close the tab in 15 seconds — and Universal Analytics would mark that as a bounce. The most valuable visit of the week looked identical to someone who closed the tab in disgust.",
      },
      {
        type: "p",
        text:
          "GA4 inverted the question. Instead of asking 'how many people bounced?' it asked 'how many actually engaged?'. The 10-second floor catches the cases where someone landed, looked, and left fast. The conversion check catches the contact-page case. The two-page check catches genuine browsing. It's a slightly fuzzier number than the old bounce rate, but it correlates much more cleanly with the thing you actually care about — visitors who got value from the visit.",
      },
      { type: "h2", text: "What counts as a 'good' engagement rate?" },
      {
        type: "p",
        text:
          "There is no universal benchmark — anyone telling you 'good engagement rate is 60%' is selling something. What matters is your engagement rate compared to your own historical average, and roughly compared to others in your niche. That said, the rough ranges we see in Plainly across thousands of small-business GA4 properties are:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Ecommerce stores: 50–70%",
            body:
              "Higher because product pages, category browsing, and add-to-cart all generate the 2-page-views condition naturally.",
          },
          {
            label: "Service business sites (single-page landing pages, contact forms): 40–60%",
            body:
              "Lower because the goal is often to get a phone call or form submit on a single page — those visits often end quickly even when they succeeded.",
          },
          {
            label: "Content sites and blogs: 55–75%",
            body:
              "Higher because readers naturally scroll, dwell, and read for longer than 10 seconds.",
          },
          {
            label: "SaaS marketing sites: 45–65%",
            body:
              "Pricing pages, feature pages, and docs drive multi-page sessions, but lots of one-click bounces from Google searches drag the rate down.",
          },
        ],
      },
      {
        type: "p",
        text:
          "If you're outside these ranges, that's a starting question, not a verdict. A 30% engagement rate isn't necessarily 'bad' if you're running a contact-only landing page where most visits genuinely take less than 10 seconds. Context first, then judgement.",
      },
      { type: "h2", text: "Three real reasons it moves week-over-week" },
      {
        type: "p",
        text:
          "When engagement rate jumps or drops by more than 5 percentage points week-over-week, you're almost always looking at one of three things:",
      },
      { type: "h3", text: "1. Your traffic mix changed" },
      {
        type: "p",
        text:
          "Different sources engage at wildly different rates. Direct traffic engages at 65-75% (people who typed your URL already know you). Organic search engages at 50-65%. Social media is the wildcard — Facebook traffic engages at 30-45%, Instagram at 25-40%, X at 40-55%. If you ran a TikTok or Facebook ad campaign last week, your overall engagement rate will drop even if your site got better, because the new traffic mix dilutes it.",
      },
      {
        type: "p",
        text:
          "Open GA4 → Reports → Acquisition → Traffic acquisition and look at engagement rate per channel. If one channel exploded in volume and that channel has a lower-than-average engagement rate, that's almost always the explanation.",
      },
      { type: "h3", text: "2. A specific page broke" },
      {
        type: "p",
        text:
          "If a high-traffic landing page suddenly takes 8 seconds to load instead of 2, or a hero image stops loading, or a redirect chain triggers, sessions land on the page, fail to render in time, and bounce within the 10-second window. Engagement rate drops, conversion rate often drops too. Check the Page speed report in GA4 (Reports → Engagement → Pages and screens, then add a 'Page load time' column) for your top 5 landing pages.",
      },
      { type: "h3", text: "3. You changed something on a key page" },
      {
        type: "p",
        text:
          "A new headline, a different above-the-fold image, a moved CTA, a new pop-up that fires immediately — any of these can shift the 10-second-or-bust threshold. If engagement rate moved the same week you shipped a site change, you probably caused it. Roll the change back to a small A/B test and watch what happens to engagement rate alone.",
      },
      { type: "h2", text: "What to do if engagement rate looks bad" },
      {
        type: "p",
        text:
          "Don't optimise the number — optimise what the number represents. Engagement rate is downstream of three things: (1) you're attracting the right people, (2) the page they land on matches what they were promised, and (3) the page loads quickly. Fix those three things and engagement rate moves on its own.",
      },
      {
        type: "p",
        text:
          "If you want a faster diagnosis, Plainly does this automatically every week. It compares your current engagement rate to last week and to the same week a year ago, identifies which channel or page moved the needle, and writes you a one-paragraph explanation in plain English. Try the free demo from the homepage and see the kind of report you'd get every Monday morning.",
      },
    ],
  },
  {
    slug: "what-is-bounce-rate-in-ga4",
    title: "What is bounce rate in GA4? (And is it really gone?)",
    description:
      "Bounce rate in GA4 is calculated as 100% minus the engagement rate — a session 'bounces' only if it lasted under 10 seconds, viewed one page, AND triggered no key event. The metric is hidden by default in GA4 but can be re-enabled by clicking the pencil icon → Metrics → +Add → Bounce rate on any standard report.",
    date: "2026-04-12",
    readMinutes: 8,
    keywords: [
      "bounce rate ga4",
      "what is bounce rate in google analytics 4",
      "ga4 bounce rate calculation",
      "is bounce rate gone in ga4",
      "how does ga4 calculate bounce rate",
      "ga4 bounce rate vs engagement rate",
      "find bounce rate ga4",
    ],
    author: { name: "Nicholas Steer", role: "Founder, Plainly" },
    content: [
      {
        type: "p",
        text:
          "If you've been told 'bounce rate is gone in GA4', you've been told half the story. Bounce rate is still in GA4. It's just hidden by default, redefined slightly, and downgraded from headline metric to optional column. This post explains what it actually means now, where to switch it back on, and whether you should even bother.",
      },
      { type: "h2", text: "The new definition: 1 minus engagement rate" },
      {
        type: "p",
        text:
          "In Universal Analytics, bounce rate meant 'sessions where the user viewed only one page'. In GA4, bounce rate is mathematically defined as 100% minus the engagement rate. That means a session 'bounces' if all three of these are true: it lasted less than 10 seconds, it didn't trigger any conversion event, and it didn't view a second page. If any one of those three is false, GA4 doesn't count it as a bounce.",
      },
      {
        type: "p",
        text:
          "Practically, this means GA4's bounce rate will almost always be lower than your old Universal Analytics bounce rate — typically by 15-30 percentage points — because the new definition is much harder to qualify for. A 78% bounce rate in UA might be a 50% bounce rate in GA4 for the exact same visitor behaviour. This isn't your site getting better. It's the ruler changing length.",
      },
      { type: "h2", text: "Where to find bounce rate in GA4" },
      {
        type: "p",
        text:
          "Google deliberately hid bounce rate to push people toward engagement rate. To bring it back as a column on any standard report:",
      },
      {
        type: "ul",
        items: [
          { label: "Step 1", body: "Open any GA4 report (e.g. Reports → Acquisition → Traffic acquisition)." },
          { label: "Step 2", body: "Click the pencil icon ✏️ in the top-right corner labelled 'Customize report'." },
          { label: "Step 3", body: "Under 'Report data', click 'Metrics'." },
          { label: "Step 4", body: "Click 'Add metric', search for 'Bounce rate', and select it." },
          { label: "Step 5", body: "Click 'Apply' then 'Save' in the right panel (you may need to 'Save as a new report' if you're not the property owner)." },
        ],
      },
      {
        type: "p",
        text:
          "From now on, bounce rate will appear as a column on that report. You'll need to repeat this for any other reports you want it on — there's no global toggle.",
      },
      { type: "h2", text: "Should you actually look at bounce rate?" },
      {
        type: "p",
        text:
          "For most small-business owners: no. Here's why.",
      },
      { type: "h3", text: "Reason 1: It's literally redundant with engagement rate" },
      {
        type: "p",
        text:
          "Since bounce rate is mathematically 100% minus engagement rate, you're looking at the same data twice — once as a positive number, once as the inverse. Tracking both is just noise. Pick one, ignore the other.",
      },
      { type: "h3", text: "Reason 2: It punishes good single-page experiences" },
      {
        type: "p",
        text:
          "A contact-only landing page that gets a phone number clicked in 8 seconds will register as a bounce under the new GA4 definition — same as the old UA definition. If your business model has lots of single-page-purpose visits (contact pages, location pages, menus, phone-click conversions), bounce rate will paint a worse picture than reality. Engagement rate (with conversion-tracking set up properly) handles this case better.",
      },
      { type: "h3", text: "Reason 3: It's not what your team or agency thinks it is" },
      {
        type: "p",
        text:
          "Almost every benchmark you find online ('industry-average bounce rate is 47%') refers to old Universal Analytics bounce rate. They don't translate to GA4. If you compare your GA4 bounce rate to those benchmarks you'll think you're winning when you're flat, or panic about a number that's actually fine.",
      },
      { type: "h2", text: "When bounce rate IS useful" },
      {
        type: "p",
        text:
          "There are two specific cases where the GA4 bounce rate is the right metric:",
      },
      {
        type: "ul",
        items: [
          {
            label: "Debugging a single landing page after a redesign",
            body:
              "Did your new homepage cause a sudden spike in bounce rate? That's a quality signal — something below the fold changed for the worse. Compare bounce rate on that specific page before/after the launch.",
          },
          {
            label: "Showing a non-marketer something simple",
            body:
              "If you have to explain GA4 to a co-founder who hates dashboards, 'bounce rate dropped from 55% to 48%' is easier to digest than 'engagement rate rose 7 percentage points'. Different brain wiring, same data.",
          },
        ],
      },
      { type: "h2", text: "The simpler thing to do" },
      {
        type: "p",
        text:
          "Pick one quality metric and watch it weekly. We recommend engagement rate because GA4 prioritises it everywhere and the benchmarks (40-65% for most small businesses) are now well-established. Spend your time on the four numbers that actually drive revenue: sessions, conversions, conversion rate, and traffic-acquisition mix. That's the entire weekly review for 95% of small-business owners.",
      },
      {
        type: "p",
        text:
          "If even that feels like too much, Plainly does the whole weekly review for you. We read your GA4 every Monday and email you a one-paragraph summary of what mattered and what to try next — bounce rate, engagement rate, and four other metrics translated into one paragraph of plain English. Try the free demo from the homepage.",
      },
    ],
  },
  {
    slug: "why-does-ga4-show-zero-conversions-this-week",
    title: "Why does GA4 say I had 0 conversions this week? (the 6 most common causes)",
    description:
      "GA4 shows zero conversions for one of six reasons: a key event was toggled off, a consent banner change reduced opt-ins, the GA4 tag stopped firing on the order page, the conversion event was renamed, the count method changed, or a filter is hiding the data. The fastest diagnosis is Admin → Key events → confirm your conversion is still listed and toggled on.",
    date: "2026-04-26",
    readMinutes: 11,
    keywords: [
      "ga4 zero conversions",
      "ga4 conversions not showing",
      "ga4 conversions missing",
      "why no conversions in ga4",
      "ga4 conversions dropped to 0",
      "ga4 key events not tracking",
      "google analytics conversions zero",
    ],
    author: { name: "Nicholas Steer", role: "Founder, Plainly" },
    content: [
      {
        type: "p",
        text:
          "Opening GA4 on Monday and seeing 'Conversions: 0' for the past week is the single most stressful moment in a small-business owner's data life. Sales clearly happened. Stripe shows the money. Shopify confirmed the orders. Your inbox has the receipts. But GA4, the source you've been told to trust, says zero. So what gives?",
      },
      {
        type: "p",
        text:
          "Ninety-five percent of the time it's not a sales problem — it's a tracking problem. This post walks through the six most common causes, in rough order of likelihood, with exactly how to diagnose each one in under 10 minutes. Run through them top-to-bottom and you'll almost certainly find your missing conversions.",
      },
      { type: "h2", text: "1. Someone renamed or deleted a key event" },
      {
        type: "p",
        text:
          "This is by far the most common cause in 2026. In GA4 you have to flag specific events as 'key events' (formerly called 'conversions') for them to show in the Conversions column. If someone — a developer, an agency, you on a tired Sunday — toggled off the 'mark as key event' switch, no new conversions will register from that moment forward. Historical numbers stay in place, but the live count freezes at the previous value.",
      },
      {
        type: "p",
        text:
          "How to check: GA4 → Admin → Data display → Key events. Confirm your conversion event (e.g. `purchase`, `generate_lead`, `form_submit`) is still in the list AND has the 'Count as key event' toggle ON. If it's missing or off, flip it back on. New conversions will start logging within the hour.",
      },
      { type: "h2", text: "2. Your consent banner got stricter" },
      {
        type: "p",
        text:
          "If you updated your cookie banner or rolled out Consent Mode v2 in late 2025 or 2026 (most EU and UK sites had to), GA4 will only count conversions from users who actively consented. If your banner now defaults to 'reject all', or if your visitors are mostly EU/UK and consent rates are low, the conversion count will plummet while sales continue normally on the backend.",
      },
      {
        type: "p",
        text:
          "How to check: GA4 → Reports → Acquisition → Traffic acquisition. Add the 'Consent mode' dimension as a secondary breakdown. If you see most users in 'Denied' or 'Not set', that's your culprit. The fix isn't to weaken the banner — it's to ensure your conversions are being modeled using GA4's Consent Mode behaviour (under Admin → Data display → Property settings → Consent settings).",
      },
      { type: "h2", text: "3. The site change accidentally killed the tag" },
      {
        type: "p",
        text:
          "A theme update, a new caching layer, a CDN switch, a Shopify app install — any of these can silently strip out the GA4 tag from key pages. The conversion event fires from the order-confirmation page, so if the tag isn't loading on that exact page, nothing gets sent.",
      },
      {
        type: "p",
        text:
          "How to check: open your site's order-confirmation page (a test purchase, or an order link from your records) in Chrome incognito. Right-click → View Page Source → search for 'gtag' or 'G-' (your measurement ID starts with G-). If you don't see your G- ID, the tag is missing. If you do see it but conversions still aren't logging, install the Google Tag Assistant browser extension and step through a test purchase — it'll show you the exact event firing or failing.",
      },
      { type: "h2", text: "4. The conversion event renamed itself" },
      {
        type: "p",
        text:
          "If you're using an ecommerce platform (Shopify, WooCommerce, BigCommerce), the conversion event name is determined by the platform's GA4 integration — and that integration can be silently updated. A Shopify app update in early 2026 changed the standard purchase event name in some configurations from `purchase` to `gtm.purchase` to `conversion`. If your GA4 'key event' was set to `purchase` but the actual event firing is now `conversion`, your key-event list is out of date.",
      },
      {
        type: "p",
        text:
          "How to check: GA4 → Reports → Engagement → Events. Look at the last 7 days. If you see an event name you don't recognise with a count roughly matching your actual sales, that's the new event name. Go to Admin → Key events and mark that one as a key event (you may also want to deactivate the old `purchase` key event so you're not double-counting if both fire).",
      },
      { type: "h2", text: "5. The conversion has a 'count method' issue" },
      {
        type: "p",
        text:
          "GA4 introduced two count methods for key events in 2025: 'Once per session' and 'Once per event'. If someone set yours to 'Once per session' but your purchase flow includes a duplicate confirmation event, only the first one fires as a conversion. Combined with a buggy tagging setup, this can show as zero conversions for an entire week.",
      },
      {
        type: "p",
        text:
          "How to check: Admin → Key events → click the row of your conversion event. Look at 'Counting method'. For ecommerce purchases, 'Once per event' is almost always the right choice (each purchase is a unique event, even from the same user). For lead form submits, 'Once per session' is usually fine.",
      },
      { type: "h2", text: "6. The date range or filter on the report is sneaky" },
      {
        type: "p",
        text:
          "Last but not embarrassingly common: the report has a sneaky filter applied. A user segment that excludes your home country (set up months ago for a campaign), a date range that ends 'yesterday' instead of 'today', or an A/B test filter that splits sessions in a way that hides one half — any of these can make conversions appear to drop to zero.",
      },
      {
        type: "p",
        text:
          "How to check: in the top-right of the report, look at the date range and any active comparisons. Click the small filter icon (if any) and confirm no segment is applied. If you're still seeing zero, switch to GA4 → Reports → Realtime → Conversions and watch in real time as someone does a test purchase. If realtime shows the conversion but the daily report doesn't, you have a report-customisation issue, not a tracking issue.",
      },
      { type: "h2", text: "The 10-minute diagnosis flow" },
      {
        type: "p",
        text:
          "Run these checks in this exact order. The first one that reveals a mismatch is your problem:",
      },
      {
        type: "ul",
        items: [
          { label: "Step 1 — Admin → Key events", body: "Is your conversion event still listed and toggled on?" },
          { label: "Step 2 — Reports → Realtime → Conversions", body: "Do a test purchase. Does it appear in realtime within 60 seconds?" },
          { label: "Step 3 — Reports → Engagement → Events", body: "Look for unexpected event names that match your sales volume." },
          { label: "Step 4 — View page source on order-confirmation page", body: "Confirm the G- measurement ID is present." },
          { label: "Step 5 — Reports → Acquisition → Traffic acquisition + Consent dimension", body: "Are most users 'Denied' or 'Not set'?" },
          { label: "Step 6 — Top-right of report", body: "Date range, comparisons, filters — anything restrictive?" },
        ],
      },
      { type: "h2", text: "How to make sure this doesn't surprise you again" },
      {
        type: "p",
        text:
          "Set up an automated weekly comparison. The reason you didn't notice your conversions had dropped to zero for a week is that nobody alerts you when a metric breaks — GA4 just quietly shows the zero. Plainly does exactly this: every Monday, it pulls your GA4 data, compares it to the previous week, and emails you a plain-English summary. If conversions drop to zero, you get an alert the same morning, not three weeks later. Try the free demo from the homepage.",
      },
    ],
  },
];

export function getPostBySlug(slug) {
  return blogPosts.find((p) => p.slug === slug);
}
